import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { appDialog } from '../utils/appDialog';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Tooltip,
  Grid,
  Container,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  FormHelperText,
  MenuItem,
  Select,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ListSubheader,
  Autocomplete,
  Switch,
  CircularProgress
} from '@mui/material';
import { useI18n } from '../i18n';
import couponService from '../services/couponService';
import { environment } from '../config/environment';
import { getPetflixAccessToken } from '../config/petflixAuth';
import { uploadImageToS3 } from '../services/s3UploadService';
import { getCompleteImagePath, extractS3Key } from '../utils/imageUtils';
import { getApiHeadersForGet } from '../utils/apiUtils';
import {
  fetchSponsorshipTypes,
  normalizeContestSponsorshipType,
  SPONSORSHIP_TYPE_NONE,
  type SponsorshipTypeConfig,
} from '../utils/sponsorshipTypesUtils';
import {
  calculateTotalPrizePool,
  distributeGrandPrize,
  distributeGrandPrizeByPlaces,
  distributeSecondaryPrizeByBands,
  clampSecondaryPrizeForPlace,
  getSecondaryBandForPlace,
  getSecondaryDistributableRange,
  validateSecondaryTotal,
  type SecondaryBandKey,
  sumGrandPrizePlaces,
  sumSecondaryPrizePool,
} from '../utils/prizeUtils';
import {
  clearDraft,
  getCreateDraftKey,
  getEditDraftKey,
  hasMeaningfulDraftContent,
  loadDraft,
  saveDraft,
  type ContestFormDraft,
} from '../utils/contestDraftUtils';
import {
  getDefaultAllowedFormats,
  getMediaDefaultsForType,
  parseAllowedFormats,
  STANDARD_IMAGE,
  STANDARD_VIDEO,
  validateAllowedFormatsSelection,
  type AllowedMediaType,
  PARTICIPANT_MEDIA_LIMIT,
} from '../utils/mediaStandards';
import {
  ALL_COUNTRIES_VALUE,
  CONTEST_COUNTRY_REGION_ORDER,
  formatAllowedCountriesLabel,
  getAllConfiguredCountryCodes,
  getCountriesByRegion,
  isAllCountriesSelection,
  normalizeCountrySelection,
  sanitizeAllowedCountries,
  validateAllowedCountriesSelection,
} from '../utils/countryEligibilityUtils';
import {
  ALL_PET_TYPES_VALUE,
  CONTEST_PET_GROUP_ORDER,
  formatAllowedPetTypesLabel,
  getAllConfiguredPetTypeCodes,
  getPetTypesByGroup,
  isAllPetTypesSelection,
  normalizePetTypeSelection,
  sanitizeAllowedPetTypes,
  validateAllowedPetTypesSelection,
} from '../utils/petEligibilityUtils';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  AttachMoney as DollarIcon,
  People as PeopleIcon,
  EmojiEvents as AwardIcon,
  CameraAlt as CameraIcon,
  Settings as SettingsIcon,
  Public as GlobeIcon,
  School as SchoolIcon,
  Schedule as ClockIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';

function normalizeAgeGroupFromDb(value: unknown): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'all_ages';
  if (raw === 'all_ages' || raw.includes('todas las edades') || raw === 'todas las edades') {
    return 'all_ages';
  }
  if (raw === 'under_18' || raw.includes('menor')) return 'under_18';
  if (raw === 'over_18' || raw.includes('mayor')) return 'over_18';
  return raw;
}

interface Props {
  onCreated?: (contestData: any) => void;
  onCreatedWithCoupons?: (contestData: any, couponConfig: any) => void;
  onCancel?: () => void;
  existingContest?: any; // Datos del concurso existente para edición
  sponsorshipData?: any; // Datos del patrocinio para pre-llenar el formulario
}

export interface CreateContestFormHandle {
  requestClose: () => Promise<boolean>;
}

const serializeFormSnapshot = (
  data: FormData,
  step: number,
  couponCodes: string[]
): string => JSON.stringify({ data, step, couponCodes });

interface FormData {
  name: string;
  description: string;
  theme_name: string;
  theme_keywords: string[];
  seasonal_theme: boolean;
  entrance_fee: number;
  organizer_buy_rate: number;
  min_participants: number;
  max_participants: number;
  members_only: boolean;
  subscribers_only: boolean;
  ticket_based_only: boolean;
  prize_type: string;
  main_prize: number;
  secondary_prize: number;
  number_of_places: number;
  grand_prize_places: number;
  secondary_prize_places: number;
  prize_first_place: number;
  prize_second_place: number;
  prize_third_place: number;
  prize_fourth_place: number;
  prize_fifth_place: number;
  prize_sixth_place: number;
  prize_seventh_place: number;
  prize_eighth_place: number;
  dynamic_prizes: Record<string, number>;
  submission_start: string;
  submission_deadline: string;
  voting_start: string;
  voting_end: string;
  winner_announcement: string;
  allowed_media_types: string;
  max_images: number;
  max_videos: number;
  max_image_size_mb: number;
  max_video_size_mb: number;
  max_video_duration_seconds: number;
  allowed_formats: string[];
  allowed_countries: string[];
  allowed_pet_types: string[];
  age_group: string;
  min_age: number | null;
  max_age: number | null;
  requires_guardian_consent: boolean;
  education_type: string;
  institution_name: string;
  academic_subject: string;
  requires_institution_verification: boolean;
  photo_rewards_mode: string;
  photo_rewards_user_percentage: number;
  photo_rewards_organizer_percentage: number;
  photo_rewards_noahs_ark_percentage: number;
  sponsorship_type: string;
  // Tipos de pase a generar por defecto para el concurso. Solo 'coupon_pass'
  // (entradas gratis) y 'entry_ticket' (Contest Pass, de pago) son editables;
  // 'vip_pass' y 'judge_invite' quedan reservados y no generan códigos aún.
  pass_types: string[];
  coupon_passes_enabled: boolean;
  coupon_uses_per_code: number;
  coupon_quantity: number;
  max_coupass: number;
  max_pass: number;
  uses_per_coupon: number;
  petflix_qr_integration: 'disabled' | 'enabled' | 'add_to_existing';
  petflix_qr_type: string;
  individual_qr: boolean;
  create_new_qr_tags: boolean;
  use_existing_codes: boolean;
  tagType: string;
  vote_type: string;
  size_vote: number;
  cover_image: string;
  winner_modal_title: string;
  winner_modal_subtitle: string;
  winner_modal_body_text: string;
  winner_modal_image: string;
  notification_templates: Record<string, string>;
  benefactor_enabled: boolean;
  benefactors: Array<{
    type: 'shelter' | 'custom';
    shelterId: string | null;
    name: string;
    category?: 'organization' | 'foundation' | 'cause' | 'rescue' | 'other';
  }>;
}

interface Errors {
  [key: string]: string | null;
}

type BenefactorItem = {
  type: 'shelter' | 'custom';
  shelterId: string | null;
  name: string;
  category?: 'organization' | 'foundation' | 'cause' | 'rescue' | 'other';
};

const BENEFACTOR_CATEGORIES: Array<{ value: BenefactorItem['category']; labelKey: string }> = [
  { value: 'organization', labelKey: 'benefactor.cat.organization' },
  { value: 'foundation',   labelKey: 'benefactor.cat.foundation' },
  { value: 'cause',        labelKey: 'benefactor.cat.cause' },
  { value: 'rescue',       labelKey: 'benefactor.cat.rescue' },
  { value: 'other',        labelKey: 'benefactor.cat.other' },
];

const CreateContestForm = forwardRef<CreateContestFormHandle, Props>(function CreateContestForm(
  { onCreated, onCreatedWithCoupons, onCancel, existingContest, sponsorshipData },
  ref
) {
  const { t, locale } = useI18n();
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [sponsorshipTypeOptions, setSponsorshipTypeOptions] = useState<SponsorshipTypeConfig[]>([]);
  const [loadingSponsorshipTypeOptions, setLoadingSponsorshipTypeOptions] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const [isButtonsAtBottom, setIsButtonsAtBottom] = useState(true);
  const buttonsContainerRef = useRef<HTMLDivElement>(null);
  const formScrollRef = useRef<HTMLDivElement>(null);

  // Detectar cambios en el ancho de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Detectar si el contenedor de botones está en su posición original
  useEffect(() => {
    const checkPosition = () => {
      if (buttonsContainerRef.current) {
        const rect = buttonsContainerRef.current.getBoundingClientRect();
        const containerRect = buttonsContainerRef.current.parentElement?.getBoundingClientRect();
        
        if (containerRect) {
          // Verificar si el contenedor está en su posición natural (no sticky)
          // Si el bottom del contenedor está cerca del bottom del padre, está en posición original
          const isAtBottom = Math.abs(rect.bottom - containerRect.bottom) < 10;
          setIsButtonsAtBottom(isAtBottom);
        }
      }
    };

    checkPosition();
    window.addEventListener('scroll', checkPosition);
    window.addEventListener('resize', checkPosition);
    
    return () => {
      window.removeEventListener('scroll', checkPosition);
      window.removeEventListener('resize', checkPosition);
    };
  }, [currentStep]);

  // Determinar si es móvil (ancho < 1000px)
  const isMobile = windowWidth < 1000;
  const [keywordInput, setKeywordInput] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [generatedCouponCodes, setGeneratedCouponCodes] = useState<string[]>([]);
  const [generatedPassCodes, setGeneratedPassCodes] = useState<string[]>([]);
  const [shelters, setShelters] = useState<Array<{_id: string, name: string}>>([]);
  const [loadingShelters, setLoadingShelters] = useState(false);
  const [couponPassesWithoutQRCount, setCouponPassesWithoutQRCount] = useState<number | null>(null);
  const [loadingCount, setLoadingCount] = useState(false);
  // Códigos reales ya enlazados al concurso (para mostrar estado en el paso CouponPasses).
  const [linkedCodesInfo, setLinkedCodesInfo] = useState<{
    loading: boolean;
    pass: { total: number; printed: number; digital: number; na: number };
    coupon: { total: number; printed: number; digital: number; na: number };
  }>({ loading: false, pass: { total: 0, printed: 0, digital: 0, na: 0 }, coupon: { total: 0, printed: 0, digital: 0, na: 0 } });
  
  // Forzar re-render cuando cambia el idioma para actualizar las traducciones
  useEffect(() => {
    // Este efecto asegura que el componente se actualice cuando cambia el locale
    // El locale está en las dependencias, así que React re-renderizará el componente
  }, [locale]);

  // Cargar el estado real de códigos ya enlazados al concurso (solo en edición y en el paso CouponPasses).
  useEffect(() => {
    const contestId = existingContest?.id;
    if (!contestId || currentStep !== 7) return;
    let ignore = false;
    const countByKind = (arr: any[]) => arr.reduce(
      (acc, c) => {
        const k = c?.code_kind;
        if (k === 'printed') acc.printed += 1;
        else if (k === 'digital') acc.digital += 1;
        else acc.na += 1;
        acc.total += 1;
        return acc;
      },
      { total: 0, printed: 0, digital: 0, na: 0 }
    );
    const load = async () => {
      setLinkedCodesInfo(prev => ({ ...prev, loading: true }));
      try {
        const [allPassCodes, allCouponPasses, allCouponCodes] = await Promise.all([
          couponService.getAllPassCodes().catch(() => []),
          couponService.getAllCouponPasses(true).catch(() => []),
          couponService.getAllCouponCodes().catch(() => [])
        ]);
        const passForContest = (allPassCodes || []).filter((pc: any) => pc.pass_id === contestId);
        const couponPassIds = new Set(
          (allCouponPasses || []).filter((cp: any) => cp.contest_id === contestId).map((cp: any) => cp.id)
        );
        const couponForContest = (allCouponCodes || []).filter((c: any) => couponPassIds.has(c.coupon_pass_id));
        if (ignore) return;
        setLinkedCodesInfo({
          loading: false,
          pass: countByKind(passForContest),
          coupon: countByKind(couponForContest)
        });
      } catch (error) {
        if (!ignore) setLinkedCodesInfo(prev => ({ ...prev, loading: false }));
      }
    };
    void load();
    return () => { ignore = true; };
  }, [existingContest?.id, currentStep]);

  // Cargar shelters desde la API de Petflix
  useEffect(() => {
    const fetchShelters = async () => {
      try {
        setLoadingShelters(true);
        const response = await fetch(`${environment.apiBaseUrl}/shelters?$limit=200`, {
          headers: getApiHeadersForGet(),
        });

        if (!response.ok) {
          throw new Error('Error fetching shelters');
        }

        const data = await response.json();
        const sheltersList = Array.isArray(data) ? data : (data.data || []);
        setShelters(sheltersList);
        
        // Buscar el shelter con name = "petContest" y establecerlo como valor por defecto
        const petContestShelter = sheltersList.find((shelter: any) => shelter.name === 'PetContest');
        if (petContestShelter) {
          setFormData(prev => {
            // Establecer el valor si no hay uno ya establecido, o si el valor es 'default' o está vacío
            const currentValue = prev.petflix_qr_type;
            const isValidValue = currentValue && sheltersList.some((s: any) => s._id === currentValue);
            
            if (!currentValue || currentValue === 'default' || !isValidValue) {
              return { ...prev, petflix_qr_type: petContestShelter._id };
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Error fetching shelters:', error);
        setShelters([]);
      } finally {
        setLoadingShelters(false);
      }
    };

    fetchShelters();
  }, []);

  // Función para generar códigos aleatorios únicos
  const generateRandomCouponCodes = (quantity: number): string[] => {
    const codes: string[] = [];
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    
    while (codes.length < quantity) {
      let code = '';
      // Generar código de 20 caracteres aleatorios
      for (let i = 0; i < 20; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      
      // Verificar que no esté duplicado
      if (!codes.includes(code)) {
        codes.push(code);
      }
    }
    
    return codes;
  };

  const generateRandomPassCodes = (quantity: number): string[] => {
    const codes: string[] = [];
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    
    while (codes.length < quantity) {
      let code = '';
      // Generar código de 20 caracteres aleatorios
      for (let i = 0; i < 20; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      
      // Verificar que no esté duplicado
      if (!codes.includes(code)) {
        codes.push(code);
      }
    }
    
    return codes;
  };
  
  // Función para mapear datos del patrocinio al formulario de concurso
  const mapSponsorshipToContestData = (sponsorship: any): Partial<FormData> => {
    if (!sponsorship) {
      console.log('❌ No hay datos de patrocinio');
      return {};
    }
    
    console.log('🔍 MAPEANDO DATOS DEL PATROCINIO:', sponsorship);
    console.log('🔍 sponsorship.schedule:', sponsorship.schedule);
    
    const mainPrize = Number(sponsorship.reward_contribution) || 250;
    console.log('💰 mainPrize (Grand Prize from sponsor):', mainPrize);
    
    const grandPrizePlaces = distributeGrandPrize(mainPrize);
    
    // Mapeo inteligente de fechas
    console.log('📅 sponsorship.schedule:', sponsorship.schedule);
    const startDate = sponsorship.schedule?.start ? new Date(sponsorship.schedule.start) : null;
    const endDate = sponsorship.schedule?.end ? new Date(sponsorship.schedule.end) : null;
    console.log('📅 startDate:', startDate);
    console.log('📅 endDate:', endDate);
    console.log('📅 startDate string:', sponsorship.schedule?.start);
    console.log('📅 endDate string:', sponsorship.schedule?.end);
    
    let submissionStart = '';
    let submissionDeadline = '';
    let votingStart = '';
    let votingEnd = '';
    let winnerAnnouncement = '';
    
    if (startDate && endDate) {
      console.log('📅 Procesando fechas...');
      
      // Convertir fechas a formato datetime-local para los campos del formulario
      const formatToDateTimeLocal = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      // Solo mapear las dos fechas principales del patrocinio
      // Fecha de inicio de envíos: misma fecha de inicio del patrocinio
      submissionStart = formatToDateTimeLocal(startDate);
      console.log('📅 submissionStart:', submissionStart);
      
      // Fecha de anuncio de ganadores: misma fecha de fin del patrocinio
      winnerAnnouncement = formatToDateTimeLocal(endDate);
      console.log('📅 winnerAnnouncement:', winnerAnnouncement);
      
      // Las otras fechas se dejan vacías para que el usuario las llene
      console.log('📅 Las fechas intermedias se dejan vacías para llenado manual');
    } else {
      console.log('❌ No se pudieron procesar las fechas - startDate o endDate es null');
    }
    
    const result = {
      main_prize: mainPrize,
      ...grandPrizePlaces,
      sponsorship_type: sponsorship.sponsorship_type || 'rewards_only',
      submission_start: submissionStart,
      submission_deadline: submissionDeadline,
      voting_start: votingStart,
      voting_end: votingEnd,
      winner_announcement: winnerAnnouncement,
    };
    
    console.log('✅ RESULTADO DEL MAPEO:', result);
    console.log('📅 Fechas mapeadas:', {
      submission_start: submissionStart,
      winner_announcement: winnerAnnouncement
    });
    return result;
  };
  
  const [formData, setFormData] = useState<FormData>(() => {
    console.log('=== INICIALIZANDO FORMULARIO DE CONCURSO ===');
    console.log('sponsorshipData en inicialización:', sponsorshipData);
    // Si hay un concurso existente, usar sus datos
    if (existingContest) {
      const formDataResult: FormData = {
        name: existingContest.name || '',
        description: existingContest.description || '',
        theme_name: existingContest.theme_name || '',
        theme_keywords: existingContest.theme_keywords || [],
        seasonal_theme: existingContest.seasonal_theme || false,
        entrance_fee: Number(existingContest.entrance_fee) || 5.00,
        organizer_buy_rate: Number(existingContest.organizer_buy_rate) || 0.00,
        min_participants: Number(existingContest.min_participants) || 24,
        max_participants: Number(existingContest.max_participants) || 100,
        members_only: existingContest.members_only || false,
        subscribers_only: existingContest.subscribers_only || false,
        ticket_based_only: existingContest.ticket_based_only || false,
        prize_type: existingContest.prize_type || 'gift card',
        main_prize: Number(existingContest.main_prize) || 250,
        secondary_prize: Number(existingContest.secondary_prize) || 0,
        number_of_places: Number(existingContest.number_of_places) || 0,
        grand_prize_places: (() => {
          const named = [
            Number(existingContest.prize_first_place) || 0,
            Number(existingContest.prize_second_place) || 0,
            Number(existingContest.prize_third_place) || 0,
            Number(existingContest.prize_fourth_place) || 0,
          ];
          const filled = named.filter((amount) => amount > 0).length;
          if (filled > 0) return filled;
          const total = Number(existingContest.number_of_places) || 0;
          return Math.min(Math.max(total, 1), 4);
        })(),
        secondary_prize_places: (() => {
          const dynamicCount = Object.keys(existingContest.dynamic_prizes || {}).length;
          if (dynamicCount > 0) return dynamicCount;
          return Math.max((Number(existingContest.number_of_places) || 0) - 4, 0);
        })(),
        prize_first_place: Number(existingContest.prize_first_place) || 125.00,
        prize_second_place: Number(existingContest.prize_second_place) || 75.00,
        prize_third_place: Number(existingContest.prize_third_place) || 37.50,
        prize_fourth_place: Number(existingContest.prize_fourth_place) || 12.50,
        prize_fifth_place: Number(existingContest.prize_fifth_place) || 0,
        prize_sixth_place: Number(existingContest.prize_sixth_place) || 0,
        prize_seventh_place: Number(existingContest.prize_seventh_place) || 0,
        prize_eighth_place: Number(existingContest.prize_eighth_place) || 0,
        dynamic_prizes: existingContest.dynamic_prizes || {},
        submission_start: existingContest.submission_start || '',
        submission_deadline: existingContest.submission_deadline || '',
        voting_start: existingContest.voting_start || '',
        voting_end: existingContest.voting_end || '',
        winner_announcement: existingContest.winner_announcement || '',
        allowed_media_types: existingContest.allowed_media_types || 'both',
        max_images: existingContest.allowed_media_types === 'video'
          ? 0
          : PARTICIPANT_MEDIA_LIMIT.maxImages,
        max_videos: existingContest.allowed_media_types === 'image'
          ? 0
          : PARTICIPANT_MEDIA_LIMIT.maxVideos,
        max_image_size_mb: Number(existingContest.max_image_size_mb) || 10,
        max_video_size_mb: Number(existingContest.max_video_size_mb) || 50,
        max_video_duration_seconds: Number(existingContest.max_video_duration_seconds) || STANDARD_VIDEO.maxDurationSeconds,
        allowed_formats: parseAllowedFormats(existingContest.allowed_formats).length
          ? parseAllowedFormats(existingContest.allowed_formats)
          : getDefaultAllowedFormats((existingContest.allowed_media_types || 'both') as AllowedMediaType),
        allowed_countries: sanitizeAllowedCountries(existingContest.allowed_countries),
        allowed_pet_types: sanitizeAllowedPetTypes(existingContest.allowed_pet_types),
        age_group: normalizeAgeGroupFromDb(existingContest.allowed_age_group),
        min_age: Number(existingContest.min_age) || null,
        max_age: Number(existingContest.max_age) || null,
        requires_guardian_consent: existingContest.requires_guardian_consent || false,
        education_type: existingContest.education_type || 'none',
        institution_name: existingContest.institution_name || '',
        academic_subject: existingContest.academic_subject || '',
        requires_institution_verification: existingContest.requires_institution_verification || false,
        photo_rewards_mode: existingContest.photo_rewards_mode || 'optional',
        photo_rewards_user_percentage: Number(existingContest.photo_rewards_user_percentage) || 80,
        photo_rewards_organizer_percentage: Number(existingContest.photo_rewards_organizer_percentage) || 10,
        photo_rewards_noahs_ark_percentage: Number(existingContest.photo_rewards_noahs_ark_percentage) || 10,
        sponsorship_type: existingContest.sponsorship_type || SPONSORSHIP_TYPE_NONE,
        pass_types: (() => {
          const raw = (existingContest as any).pass_types;
          const allowed = ['coupon_pass', 'entry_ticket', 'vip_pass', 'judge_invite'];
          let list: string[] = [];
          if (Array.isArray(raw)) list = raw;
          else if (typeof raw === 'string' && raw.trim()) {
            try { const p = JSON.parse(raw); list = Array.isArray(p) ? p : [raw]; } catch { list = [raw]; }
          }
          const cleaned = list.filter((t) => allowed.includes(t));
          return cleaned.length ? cleaned : ['coupon_pass', 'entry_ticket'];
        })(),
        coupon_passes_enabled: existingContest.coupon_passes_enabled || false,
        coupon_uses_per_code: Number(existingContest.coupon_uses_per_code) || 3,
        coupon_quantity: Number(existingContest.coupon_quantity) || 10,
        max_coupass: Number(existingContest.max_coupass) || 0,
        max_pass: Number(existingContest.max_pass) || 0,
        uses_per_coupon: Number(existingContest.uses_per_coupon) || 1,
        petflix_qr_integration: (() => {
          const value = existingContest.petflix_qr_integration;
          if (value === true || value === 'enabled') return 'enabled' as const;
          if (value === 'add_to_existing') return 'add_to_existing' as const;
          return 'disabled' as const;
        })(),
        petflix_qr_type: existingContest.petflix_qr_type || '',
        individual_qr: existingContest.individual_qr || false,
        create_new_qr_tags: existingContest.create_new_qr_tags || false,
        use_existing_codes: existingContest.use_existing_codes || false,
        tagType: existingContest.tagType || 'standard',
        vote_type: existingContest.vote_type || existingContest.Vote_Type || 'users',
        size_vote: Number(existingContest.size_vote) || Number(existingContest.Size_Vote) || 1,
        cover_image: existingContest.cover_image || '',
        winner_modal_title: existingContest.winner_modal_title || '',
        winner_modal_subtitle: existingContest.winner_modal_subtitle || '',
        winner_modal_body_text: existingContest.winner_modal_body_text || '',
        winner_modal_image: existingContest.winner_modal_image || '',
        notification_templates: existingContest.notification_templates || {},
        benefactor_enabled: !!existingContest.benefactor_enabled,
        benefactors: existingContest.benefactors || [],
      } as FormData;
      return formDataResult;
    }
    
    // Si no hay concurso existente, usar valores por defecto y datos del patrocinio si están disponibles
    const mediaDefaults = getMediaDefaultsForType('both');
    const defaultData = {
      name: '',
      description: '',
      theme_name: '',
      theme_keywords: [],
      seasonal_theme: false,
      entrance_fee: 5.00,
      organizer_buy_rate: 0.00,
      min_participants: 24,
      max_participants: 100,
      members_only: false,
      subscribers_only: false,
      ticket_based_only: false,
      prize_type: 'gift card',
      main_prize: 250,
      secondary_prize: 0,
      number_of_places: 4,
      grand_prize_places: 4,
      secondary_prize_places: 0,
      prize_first_place: 125.00,
      prize_second_place: 75.00,
      prize_third_place: 37.50,
      prize_fourth_place: 12.50,
      prize_fifth_place: 0,
      prize_sixth_place: 0,
      prize_seventh_place: 0,
      prize_eighth_place: 0,
      dynamic_prizes: {},
      submission_start: '',
      submission_deadline: '',
      voting_start: '',
      voting_end: '',
      winner_announcement: '',
      allowed_media_types: 'both',
      max_images: mediaDefaults.max_images,
      max_videos: mediaDefaults.max_videos,
      max_image_size_mb: mediaDefaults.max_image_size_mb,
      max_video_size_mb: mediaDefaults.max_video_size_mb,
      max_video_duration_seconds: mediaDefaults.max_video_duration_seconds,
      allowed_formats: mediaDefaults.allowed_formats,
      allowed_countries: ['all'],
      allowed_pet_types: ['all'],
      age_group: 'all_ages',
      min_age: null,
      max_age: null,
      requires_guardian_consent: false,
      education_type: 'none',
      institution_name: '',
      academic_subject: '',
      requires_institution_verification: false,
      photo_rewards_mode: 'optional',
      photo_rewards_user_percentage: 80,
      photo_rewards_organizer_percentage: 10,
      photo_rewards_noahs_ark_percentage: 10,
      sponsorship_type: SPONSORSHIP_TYPE_NONE,
      pass_types: ['coupon_pass', 'entry_ticket'],
      coupon_passes_enabled: true,
      coupon_uses_per_code: 3,
      coupon_quantity: 10,
      max_coupass: 0,
      max_pass: 0,
      uses_per_coupon: 1,
      petflix_qr_integration: 'disabled',
      petflix_qr_type: '',
      individual_qr: false,
      create_new_qr_tags: false,
      use_existing_codes: false,
      tagType: 'standard',
      vote_type: 'users',
      size_vote: 1,
      cover_image: '',
      winner_modal_title: '',
      winner_modal_subtitle: '',
      winner_modal_body_text: '',
      winner_modal_image: '',
      notification_templates: {},
      benefactor_enabled: false,
      benefactors: [],
    };
    
    // Combinar datos por defecto con datos del patrocinio si están disponibles
    console.log('=== INICIALIZANDO FORMULARIO DE CONCURSO ===');
    console.log('sponsorshipData en inicialización:', sponsorshipData);
    const sponsorshipMappedData = sponsorshipData ? mapSponsorshipToContestData(sponsorshipData) : {};
    console.log('sponsorshipMappedData:', sponsorshipMappedData);
    
    const finalData = {
      ...defaultData,
      ...sponsorshipMappedData
    };
    
    console.log('Datos finales del formulario:', finalData);
    return finalData;
  });

  // Benefactores ya guardados en pet-contests (reutilizar entre concursos)
  const [savedBenefactors, setSavedBenefactors] = useState<Array<{type: string, shelter_id: string | null, name: string, category: string | null}>>([]);
  const [loadingSavedBenefactors, setLoadingSavedBenefactors] = useState(false);

  // Estado del input para crear un benefactor (organización / causa) nuevo.
  // Se eleva al padre para poder "vaciar" el texto pendiente al guardar el
  // concurso, evitando que un nombre escrito pero no agregado se pierda.
  const [newBenefactorName, setNewBenefactorName] = useState('');
  const [newBenefactorCategory, setNewBenefactorCategory] =
    useState<BenefactorItem['category']>('organization');
  const [newBenefactorError, setNewBenefactorError] = useState('');

  const loadSavedBenefactors = React.useCallback(async () => {
    setLoadingSavedBenefactors(true);
    try {
      // Preferir /api/benefactors; si no existe (servidor sin reiniciar), extraer de /api/contests
      let list: Array<{type: string, shelter_id: string | null, name: string, category: string | null}> = [];

      const directRes = await fetch(`${environment.apiBaseUrl}/benefactors`, {
        headers: getApiHeadersForGet(),
      });
      if (directRes.ok) {
        const directData = await directRes.json();
        if (Array.isArray(directData)) {
          list = directData;
        }
      }

      if (list.length === 0) {
        const contestsRes = await fetch(`${environment.apiBaseUrl}/contests?_t=${Date.now()}`, {
          headers: getApiHeadersForGet(),
        });
        if (contestsRes.ok) {
          const contestsData = await contestsRes.json();
          const contests = Array.isArray(contestsData) ? contestsData : (contestsData.data || []);
          const seen = new Set<string>();
          for (const contest of contests) {
            for (const b of contest.benefactors || []) {
              if (!b?.name) continue;
              const key = `${b.type}:${b.name}`;
              if (seen.has(key)) continue;
              seen.add(key);
              list.push({
                type: b.type,
                shelter_id: b.shelterId || null,
                name: b.name,
                category: b.category || null,
              });
            }
          }
          list.sort((a, b) => a.name.localeCompare(b.name));
        }
      }

      setSavedBenefactors(list);
    } catch {
      setSavedBenefactors([]);
    } finally {
      setLoadingSavedBenefactors(false);
    }
  }, []);

  useEffect(() => {
    if (formData.benefactor_enabled) {
      loadSavedBenefactors();
    }
  }, [formData.benefactor_enabled, loadSavedBenefactors]);

  const isEditMode = Boolean(existingContest?.id);
  const coverImagePreviewUrl = formData.cover_image
    ? getCompleteImagePath(formData.cover_image) || formData.cover_image
    : null;

  useEffect(() => {
    let cancelled = false;

    const loadSponsorshipTypeOptions = async () => {
      setLoadingSponsorshipTypeOptions(true);
      try {
        const types = await fetchSponsorshipTypes(environment.apiBaseUrl, getApiHeadersForGet(), t);
        if (!cancelled) {
          setSponsorshipTypeOptions(types);
        }
      } catch (error) {
        console.error('Error loading sponsorship type options:', error);
      } finally {
        if (!cancelled) {
          setLoadingSponsorshipTypeOptions(false);
        }
      }
    };

    loadSponsorshipTypeOptions();
    return () => {
      cancelled = true;
    };
  }, [t, locale]);

  useEffect(() => {
    if (loadingSponsorshipTypeOptions) return;
    const availableValues = sponsorshipTypeOptions.map((type) => type.value);
    setFormData((prev) => {
      const normalized = normalizeContestSponsorshipType(prev.sponsorship_type, availableValues);
      if (normalized === prev.sponsorship_type) return prev;
      return { ...prev, sponsorship_type: normalized };
    });
  }, [loadingSponsorshipTypeOptions, sponsorshipTypeOptions]);

  const draftStorageKey = existingContest?.id
    ? getEditDraftKey(existingContest.id)
    : getCreateDraftKey();

  const initialSnapshotRef = useRef<string | null>(null);
  const closeResolveRef = useRef<((value: boolean) => void) | null>(null);
  const [restoreDraftOpen, setRestoreDraftOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<ContestFormDraft | null>(null);

  if (initialSnapshotRef.current === null) {
    initialSnapshotRef.current = serializeFormSnapshot(formData, currentStep, generatedCouponCodes);
  }

  const isFormDirty = useCallback(() => {
    const baseline = initialSnapshotRef.current ?? '';
    return serializeFormSnapshot(formData, currentStep, generatedCouponCodes) !== baseline;
  }, [formData, currentStep, generatedCouponCodes]);

  const hasDraftContent = useCallback(() => {
    return hasMeaningfulDraftContent(
      formData as unknown as Record<string, unknown>,
      currentStep
    );
  }, [formData, currentStep]);

  useEffect(() => {
    if (existingContest || sponsorshipData) return;
    const draft = loadDraft(getCreateDraftKey());
    if (draft && hasMeaningfulDraftContent(draft.formData, draft.currentStep)) {
      setPendingDraft(draft);
      setRestoreDraftOpen(true);
    }
  }, [existingContest, sponsorshipData]);

  useEffect(() => {
    if (!hasDraftContent()) return;
    const timer = window.setTimeout(() => {
      saveDraft(draftStorageKey, {
        formData: formData as unknown as Record<string, unknown>,
        currentStep,
        generatedCouponCodes,
      });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [formData, currentStep, generatedCouponCodes, draftStorageKey, hasDraftContent]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isFormDirty() || !hasDraftContent()) return;
      saveDraft(draftStorageKey, {
        formData: formData as unknown as Record<string, unknown>,
        currentStep,
        generatedCouponCodes,
      });
      event.preventDefault();
      event.returnValue = t('contestformdraft.beforeunload');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData, currentStep, generatedCouponCodes, draftStorageKey, isFormDirty, hasDraftContent, t]);

  const finishCloseRequest = useCallback(
    (canClose: boolean, action?: 'save' | 'discard') => {
      if (action === 'save' && hasDraftContent()) {
        saveDraft(draftStorageKey, {
          formData: formData as unknown as Record<string, unknown>,
          currentStep,
          generatedCouponCodes,
        });
      } else if (action === 'discard') {
        clearDraft(draftStorageKey);
      }
      setCloseConfirmOpen(false);
      closeResolveRef.current?.(canClose);
      closeResolveRef.current = null;
    },
    [formData, currentStep, generatedCouponCodes, draftStorageKey, hasDraftContent]
  );

  useImperativeHandle(
    ref,
    () => ({
      requestClose: () =>
        new Promise<boolean>((resolve) => {
          if (!isFormDirty() || !hasDraftContent()) {
            resolve(true);
            return;
          }
          closeResolveRef.current = resolve;
          setCloseConfirmOpen(true);
        }),
    }),
    [isFormDirty, hasDraftContent]
  );

  const handleRestoreDraft = () => {
    if (!pendingDraft) return;
    setFormData(pendingDraft.formData as FormData);
    setCurrentStep(pendingDraft.currentStep);
    if (pendingDraft.generatedCouponCodes?.length) {
      setGeneratedCouponCodes(pendingDraft.generatedCouponCodes);
    }
    setRestoreDraftOpen(false);
    setPendingDraft(null);
  };

  const handleDiscardDraft = () => {
    clearDraft(getCreateDraftKey());
    setRestoreDraftOpen(false);
    setPendingDraft(null);
  };

  // Actualizar formulario cuando cambie sponsorshipData
  useEffect(() => {
    if (sponsorshipData && !existingContest) {
      console.log('🔄 ACTUALIZANDO FORMULARIO CON DATOS DEL PATROCINIO');
      console.log('📊 sponsorshipData:', sponsorshipData);
      const sponsorshipMappedData = mapSponsorshipToContestData(sponsorshipData);
      console.log('📊 sponsorshipMappedData:', sponsorshipMappedData);
      
      setFormData(prev => {
        const newData = {
          ...prev,
          ...sponsorshipMappedData
        };
        console.log('📊 Datos actualizados del formulario:', newData);
        return newData;
      });
    }
  }, [sponsorshipData, existingContest]);

  // Generar códigos de cupón y pass cuando cambie la cantidad o el ID
  useEffect(() => {
    console.log('=== DEBUGGING CÓDIGOS DE CUPÓN Y PASS ===');
    console.log('formData:', formData);
    console.log('coupon_passes_enabled:', formData?.coupon_passes_enabled);
    console.log('coupon_quantity:', formData?.coupon_quantity);
    console.log('max_participants:', formData?.max_participants);
    // console.log('formData.id:', formData?.id); // Removed - id no existe en FormData
    
    if (formData && formData.coupon_passes_enabled) {
      const couponQuantity = formData.coupon_quantity || 10;
      const maxParticipants = formData.max_participants || 100;
      
      // Sincronizar uses_per_coupon con number_of_uses_per_coupon (coupon_uses_per_code)
      const usesPerCoupon = formData.coupon_uses_per_code || 3;
      if (formData.uses_per_coupon !== usesPerCoupon) {
        updateFormData('uses_per_coupon', usesPerCoupon);
      }

      // max_coupass debe tomar el valor de coupon_quantity (Number of Coupons to Generate)
      if (formData.max_coupass !== couponQuantity) {
        updateFormData('max_coupass', couponQuantity);
      }
      
      // max_pass = max_participants (solo el máximo de participantes, sin restar)
      if (formData.max_pass !== maxParticipants) {
        updateFormData('max_pass', maxParticipants);
      }
      
      // Solo se pre-generan los códigos de los tipos de pase seleccionados.
      const selectedTypes = Array.isArray(formData.pass_types) ? formData.pass_types : [];
      const wantCoupon = selectedTypes.includes('coupon_pass');
      const wantContestPass = selectedTypes.includes('entry_ticket');

      // Coupon Pass (entradas gratuitas)
      if (wantCoupon) {
        console.log('Generando códigos de cupón, Cantidad:', couponQuantity);
        const couponCodes = generateRandomCouponCodes(couponQuantity);
        console.log('Códigos de cupón generados:', couponCodes.length);
        setGeneratedCouponCodes(couponCodes);
      } else {
        console.log('Coupon Pass no seleccionado: no se generan códigos de cupón');
        setGeneratedCouponCodes([]);
      }

      // Contest Pass (entradas con valor) → Pass_Codes, cantidad = max_participants
      const passCodesQuantity = maxParticipants;
      if (wantContestPass && passCodesQuantity > 0) {
        console.log('Generando códigos de Contest Pass, Cantidad:', passCodesQuantity);
        const passCodes = generateRandomPassCodes(passCodesQuantity);
        console.log('Códigos de Pass generados:', passCodes.length);
        setGeneratedPassCodes(passCodes);
      } else {
        console.log('Contest Pass no seleccionado (o cantidad 0): no se generan Pass_Codes');
        setGeneratedPassCodes([]);
      }
    } else {
      console.log('No se generan códigos - CouponPasses deshabilitado o formData no disponible');
      setGeneratedCouponCodes([]);
      setGeneratedPassCodes([]);
    }
  }, [formData?.coupon_passes_enabled, formData?.coupon_quantity, formData?.max_participants, formData?.pass_types]);

  const steps = [
    { id: 1, name: t('stepsContestForm.basic'), icon: SettingsIcon },
    { id: 2, name: t('stepsContestForm.prizes'), icon: AwardIcon },
    { id: 3, name: t('stepsContestForm.dates'), icon: CalendarIcon },
    { id: 4, name: t('stepsContestForm.media'), icon: CameraIcon },
    { id: 5, name: t('stepsContestForm.eligibility'), icon: GlobeIcon },
    { id: 6, name: t('stepsContestForm.advanced'), icon: InfoIcon },
    { id: 7, name: 'CouponPasses', icon: InfoIcon }
  ];

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Opciones para el selector "benefactores previos": combina los benefactores
  // guardados en la BD (reutilizables entre concursos) con los que ya están
  // seleccionados en este formulario (incluye los recién creados), de modo que
  // aparezcan de inmediato en la lista sin necesidad de recargar.
  const benefactorOptions = React.useMemo<BenefactorItem[]>(() => {
    const options: BenefactorItem[] = savedBenefactors.map((s) => ({
      type: s.type === 'shelter' ? 'shelter' : 'custom',
      shelterId: s.shelter_id ?? null,
      name: s.name,
      category: (s.category ?? undefined) as BenefactorItem['category'],
    }));
    for (const b of formData.benefactors) {
      if (!options.some((o) => o.name === b.name && o.type === b.type)) {
        options.push(b);
      }
    }
    return options;
  }, [savedBenefactors, formData.benefactors]);

  // Agrega un benefactor personalizado (organización / causa) al formulario y a
  // la lista de "previos" para que sea reutilizable de inmediato. Devuelve el
  // item creado o null si el nombre es inválido.
  const addCustomBenefactor = (
    rawName: string,
    category: BenefactorItem['category']
  ): BenefactorItem | null => {
    const trimmed = rawName.trim();
    if (!trimmed) {
      setNewBenefactorError(t('benefactor.no_name_error'));
      return null;
    }
    const alreadyAdded = formData.benefactors.some(
      (b) => b.type === 'custom' && b.name.toLowerCase() === trimmed.toLowerCase()
    );
    const item: BenefactorItem = { type: 'custom', shelterId: null, name: trimmed, category };
    if (!alreadyAdded) {
      updateFormData('benefactors', [...formData.benefactors, item]);
    }
    setSavedBenefactors((prev) =>
      prev.some((s) => s.type === 'custom' && s.name.toLowerCase() === trimmed.toLowerCase())
        ? prev
        : [...prev, { type: 'custom', shelter_id: null, name: trimmed, category: category ?? null }]
    );
    setNewBenefactorName('');
    setNewBenefactorError('');
    return item;
  };

  // Lugares principales del Gran Premio (campos nombrados, máximo 4).
  const GRAND_PRIZE_FIELDS: (keyof FormData)[] = [
    'prize_first_place',
    'prize_second_place',
    'prize_third_place',
    'prize_fourth_place',
  ];

  const sumGrandFields = (data: FormData, places: number): number =>
    GRAND_PRIZE_FIELDS.slice(0, places).reduce(
      (sum, fieldName) => sum + (Number(data[fieldName] as number) || 0),
      0
    );

  // Texto en crudo de los inputs de precio mientras el usuario escribe. Permite
  // teclear con naturalidad (ej. "12.", "0.05") sin que el formateo interfiera.
  const [priceText, setPriceText] = useState<Record<string, string>>({});
  const getPriceText = (key: string, numericValue: number): string => {
    if (priceText[key] !== undefined) return priceText[key];
    return numericValue ? String(numericValue) : '';
  };

  // Deja solo dígitos y un único punto decimal (para montos en USD).
  const sanitizeMoney = (raw: string): string => {
    let v = String(raw).replace(/[^0-9.]/g, '');
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    }
    return v;
  };

  // Deja solo dígitos (para contadores de lugares).
  const sanitizeInt = (raw: string): string => String(raw).replace(/[^0-9]/g, '');

  const setRawText = (key: string, value: string) =>
    setPriceText(prev => ({ ...prev, [key]: value }));

  const clearRawText = (key: string) =>
    setPriceText(prev => {
      if (prev[key] === undefined) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  // Autocompletado del Gran Premio: reparte el TOTAL entre los lugares activos
  // (1..4) según la distribución estándar y sincroniza el texto visible de cada
  // input. Al escribir el total cada lugar se llena solo; el usuario puede
  // ajustar cualquier monto manualmente después.
  const applyGrandPrizeDistribution = (total: number, places: number) => {
    const count = Math.min(Math.max(Math.floor(places) || 1, 1), 4);
    const distributed = distributeGrandPrizeByPlaces(Number(total) || 0, count);
    const amounts = [
      distributed.prize_first_place,
      distributed.prize_second_place,
      distributed.prize_third_place,
      distributed.prize_fourth_place,
    ];
    setFormData(prev => {
      const updated: FormData = { ...prev };
      GRAND_PRIZE_FIELDS.forEach((fieldName, idx) => {
        (updated[fieldName] as number) = idx < count ? amounts[idx] : 0;
      });
      return updated;
    });
    setPriceText(prev => {
      const next = { ...prev };
      GRAND_PRIZE_FIELDS.forEach((fieldName, idx) => {
        if (idx < count) {
          const v = amounts[idx];
          next[fieldName as string] = v ? String(Number(v.toFixed(2))) : '';
        } else {
          delete next[fieldName as string];
        }
      });
      return next;
    });
    if (errors.prizes) setErrors(prev => ({ ...prev, prizes: null }));
  };

  // Cambia el Gran Premio total y AUTOCOMPLETA los montos por lugar.
  const setMainPrizeWithDistribution = (rawValue: string) => {
    const total = rawValue === '' ? 0 : parseFloat(rawValue) || 0;
    setPriceText(prev => ({ ...prev, main_prize: rawValue }));
    updateFormData('main_prize', total);
    applyGrandPrizeDistribution(total, formData.grand_prize_places);
  };

  // Define cuántos lugares tiene el Gran Premio (1..4) y RE-REPARTE el total
  // actual entre esos lugares (autocompletado consciente del número de lugares).
  const setGrandPrizePlaces = (rawCount: number) => {
    const count = Math.min(Math.max(Math.floor(rawCount) || 0, 1), 4);
    setFormData(prev => ({
      ...prev,
      grand_prize_places: count,
      number_of_places: count + prev.secondary_prize_places,
    }));
    applyGrandPrizeDistribution(formData.main_prize, count);
  };

  // Cambia el monto de un lugar del Gran Premio (override manual, sin recálculos).
  const setGrandPlaceAmount = (idx: number, value: number) => {
    const fieldName = GRAND_PRIZE_FIELDS[idx];
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (errors.prizes) setErrors(prev => ({ ...prev, prizes: null }));
  };

  // Autocompletado del Premio Secundario: reparte el TOTAL entre bandas y dentro
  // de cada banda baja de forma gradual (lugar #1 > #2 > …), no todos al máximo.
  const SECONDARY_PLACE_KEY = (index: number) => `prize_place_${5 + index}`;
  const MAX_SECONDARY_PLACES = 100;

  const applySecondaryPrizeDistribution = (total: number, places: number) => {
    const count = Math.min(Math.max(Math.floor(places) || 0, 0), MAX_SECONDARY_PLACES);
    const amounts = distributeSecondaryPrizeByBands(Number(total) || 0, count);
    setFormData(prev => {
      const newDynamic: Record<string, number> = {};
      for (let i = 0; i < count; i++) {
        newDynamic[SECONDARY_PLACE_KEY(i)] = amounts[i] || 0;
      }
      return {
        ...prev,
        secondary_prize_places: count,
        dynamic_prizes: newDynamic,
        number_of_places: prev.grand_prize_places + count,
      };
    });
    setPriceText(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (/^prize_place_\d+$/.test(key)) delete next[key];
      });
      for (let i = 0; i < count; i++) {
        const v = amounts[i] || 0;
        next[SECONDARY_PLACE_KEY(i)] = v ? String(Number(v.toFixed(2))) : '';
      }
      return next;
    });
    if (errors.prizes) setErrors(prev => ({ ...prev, prizes: null }));
  };

  // Cambia el Premio Secundario total y AUTOCOMPLETA cada lugar con la campana.
  const setSecondaryPrizeWithDistribution = (rawValue: string) => {
    const total = rawValue === '' ? 0 : parseFloat(rawValue) || 0;
    setRawText('secondary_prize', rawValue);
    updateFormData('secondary_prize', total);
    applySecondaryPrizeDistribution(total, formData.secondary_prize_places);
  };

  // Define cuántos lugares secundarios hay y RE-REPARTE el total actual entre
  // ellos por bandas (autocompletado consciente del número de lugares).
  const setSecondaryPrizePlaces = (rawCount: number) => {
    const count = Math.min(Math.max(Math.floor(rawCount) || 0, 0), MAX_SECONDARY_PLACES);
    applySecondaryPrizeDistribution(formData.secondary_prize, count);
  };

  // Cambia el monto de un lugar secundario (override manual, sin recálculos).
  const setSecondaryPlaceAmount = (placeKey: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      dynamic_prizes: { ...prev.dynamic_prizes, [placeKey]: value },
    }));
  };

  const addKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !formData.theme_keywords.includes(trimmed)) {
      updateFormData('theme_keywords', [...formData.theme_keywords, trimmed]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    updateFormData('theme_keywords', formData.theme_keywords.filter(k => k !== keyword));
  };

  const validateStep = (step: number) => {
    const newErrors: Errors = {};
    
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = t('basicinfo.name_required');
      if (!formData.description.trim()) newErrors.description = t('basicinfo.description_required');
      if (formData.entrance_fee < 0) newErrors.entrance_fee = t('basicinfo.entrance_fee_positive');
      if (formData.min_participants < 1) newErrors.min_participants = t('basicinfo.min_participants_required');
      if (formData.max_participants < formData.min_participants) newErrors.max_participants = 'El máximo de participantes debe ser mayor o igual al mínimo';
    }
    
    if (step === 2) {
      const total = formData.prize_first_place + formData.prize_second_place + formData.prize_third_place + formData.prize_fourth_place;
      if (total <= 0) newErrors.prizes = 'Debe haber al menos un premio';
      
      // Validar que el premio principal sea mayor al secundario (solo al presionar seguir)
      if (formData.secondary_prize > 0 && formData.main_prize > 0 && formData.secondary_prize >= formData.main_prize) {
        newErrors.secondary_prize = '⚠️ Advertencia: El premio secundario debe ser menor al premio principal';
      }
      
      // Validar que el premio principal sea mayor al secundario (solo al presionar seguir)
      if (formData.main_prize > 0 && formData.secondary_prize > 0 && formData.main_prize <= formData.secondary_prize) {
        newErrors.main_prize = t('prizesinfo.main_prize_warning');
      }
    }
    
    if (step === 3) {
      if (!formData.submission_start) newErrors.submission_start = t('datesinfo.submission_start_required');
      if (!formData.submission_deadline) newErrors.submission_deadline = t('datesinfo.submission_deadline_required');
      if (!formData.winner_announcement) newErrors.winner_announcement = t('datesinfo.winner_announcement_required');
      
      if (formData.submission_start && formData.submission_deadline) {
        if (new Date(formData.submission_deadline) <= new Date(formData.submission_start)) {
          newErrors.submission_deadline = 'Debe ser después del inicio';
        }
      }
      if (formData.submission_deadline && formData.winner_announcement) {
        if (new Date(formData.winner_announcement) <= new Date(formData.submission_deadline)) {
          newErrors.winner_announcement = 'Debe ser después de la fecha límite';
        }
      }
    }

    if (step === 4) {
      const mediaType = formData.allowed_media_types as AllowedMediaType;
      const formatValidation = validateAllowedFormatsSelection(formData.allowed_formats, mediaType);
      if (!formatValidation.valid) {
        newErrors.allowed_formats = t('mediainfo.allowed_formats_required');
      }
    }

    if (step === 5) {
      const countryValidation = validateAllowedCountriesSelection(formData.allowed_countries);
      if (!countryValidation.valid) {
        newErrors.allowed_countries = t('eligibilityinfo.allowed_countries_required');
      }

      const petValidation = validateAllowedPetTypesSelection(formData.allowed_pet_types);
      if (!petValidation.valid) {
        newErrors.allowed_pet_types = t('eligibilityinfo.allowed_pet_types_required');
      }

      if (
        formData.min_age != null &&
        formData.max_age != null &&
        Number(formData.min_age) > Number(formData.max_age)
      ) {
        newErrors.max_age = t('eligibilityinfo.max_age_must_be_greater');
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
      // Scroll al inicio del formulario (útil cuando está dentro de un modal con scroll)
      requestAnimationFrame(() => {
        formScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Scroll al inicio del formulario (útil cuando está dentro de un modal con scroll)
    requestAnimationFrame(() => {
      formScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleSubmit = async () => {
    if (validateStep(currentStep)) {
      const prizeTotal = calculateTotalPrizePool(formData);
      const mediaDefaults = getMediaDefaultsForType(formData.allowed_media_types as AllowedMediaType);
      const sanitizedPetTypes = sanitizeAllowedPetTypes(formData.allowed_pet_types);
      const sanitizedCountries = sanitizeAllowedCountries(formData.allowed_countries);
      
      const contestData = {
        ...formData,
        max_images: mediaDefaults.max_images,
        max_videos: mediaDefaults.max_videos,
        allowed_formats: formData.allowed_formats.map((format) => format.toLowerCase()),
        allowed_countries: sanitizedCountries,
        allowed_pet_types: sanitizedPetTypes,
        // Alinear campos derivados con lo solicitado:
        // max_coupass debe tomar el valor de coupon_quantity (Number of Coupons to Generate)
        // uses_per_coupon debe tomar el dato de number_of_uses_per_coupon (coupon_uses_per_code)
        uses_per_coupon: formData.coupon_uses_per_code || 1,
        max_coupass: formData.coupon_quantity || 0, // max_coupass = coupon_quantity
        max_pass: formData.max_participants || formData.max_pass || 0, // max_pass = max_participants
        allowed_age_group: formData.age_group, // Mapear age_group a allowed_age_group
        prize_pool_total: prizeTotal,
        ...(existingContest
          ? {
              status: existingContest.status,
              phase: existingContest.phase,
              current_participants: existingContest.current_participants ?? 0,
              ops_cost: existingContest.ops_cost ?? 50.0,
              s3_bucket_name: existingContest.s3_bucket_name,
              s3_folder_prefix: existingContest.s3_folder_prefix,
            }
          : {
              status: 'pending',
              phase: 'upcoming',
              current_participants: 0,
              ops_cost: 50.0,
              s3_bucket_name: 'petcontest-prod-media',
              s3_folder_prefix: 'contests/contest_' + Date.now() + '/',
            }),
        generatedCouponCodes: generatedCouponCodes,
        generatedPassCodes: generatedPassCodes
      };

      if (!contestData.benefactor_enabled) {
        contestData.benefactors = [];
      } else {
        // Vaciar el texto pendiente del input de organización/causa: si el
        // usuario escribió un nombre y presionó "Guardar" sin pulsar "Agregar",
        // igual lo incluimos para no perder el benefactor.
        const pending = newBenefactorName.trim();
        if (
          pending &&
          !contestData.benefactors.some(
            (b) => b.type === 'custom' && b.name.toLowerCase() === pending.toLowerCase()
          )
        ) {
          contestData.benefactors = [
            ...contestData.benefactors,
            { type: 'custom', shelterId: null, name: pending, category: newBenefactorCategory },
          ];
        }
      }
      
      console.log('Contest Data:', contestData);
      
      clearDraft(draftStorageKey);
      initialSnapshotRef.current = serializeFormSnapshot(formData, currentStep, generatedCouponCodes);
      
      // Llamada a la API de Petflix para crear el post-tag
      // Solo crear el tag cuando se crea un nuevo concurso, no cuando se edita
      let organizerId = null;
      
      // Si estamos editando un concurso existente, usar su organizer_id si existe
      if (existingContest && existingContest.organizer_id) {
        organizerId = existingContest.organizer_id;
        (contestData as any).organizer_id = organizerId;
        console.log(`ℹ️ Editando concurso existente. Usando organizer_id existente: ${organizerId}`);
      } else if (!existingContest) {
        // Solo crear el tag cuando se crea un nuevo concurso
        try {
          const petflixToken = getPetflixAccessToken();
          const petflixPostTagData = {
            name: formData.name || "Social Test",
            active: true,
            visibleInFeed: true,
            premium: false,
            parent: "6777ab7999109cf1c8de8a7c"
          };

          const apiUrl = `${environment.petflixApiUrl}/post-tags`;
          
          console.log('=== PETFLIX API - POST /post-tags (CREANDO NUEVO CONCURSO) ===');
          console.log('URL:', apiUrl);
          console.log('Datos enviados:', JSON.stringify(petflixPostTagData, null, 2));
          console.log('Headers:', {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${petflixToken ? petflixToken.substring(0, 20) + '...' : 'NO TOKEN'}`
          });

          const petflixResponse = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${petflixToken}`
            },
            body: JSON.stringify(petflixPostTagData)
          });

          console.log('Status Code:', petflixResponse.status);
          console.log('Status Text:', petflixResponse.statusText);

          if (petflixResponse.ok) {
            const petflixResult = await petflixResponse.json();
            console.log('✅ Respuesta exitosa de Petflix API:');
            console.log(JSON.stringify(petflixResult, null, 2));
            
            // Extraer el _id de la respuesta
            if (petflixResult._id) {
              organizerId = petflixResult._id;
              console.log(`✅ Organizer ID obtenido de post-tags: ${organizerId}`);
              // Agregar el organizer_id al contestData
              (contestData as any).organizer_id = organizerId;
            } else {
              console.warn('⚠️ La respuesta de post-tags no contiene _id');
            }
          } else {
            const errorData = await petflixResponse.json().catch(() => ({ message: 'Error desconocido' }));
            console.error('❌ Error en respuesta de Petflix API:');
            console.error(JSON.stringify(errorData, null, 2));
          }
        } catch (error) {
          console.error('❌ Error en la llamada a Petflix API:');
          console.error(error);
        }
      } else {
        console.log('ℹ️ Editando concurso sin organizer_id. No se creará nuevo tag.');
      }
      
      if (onCreated) {
        onCreated(contestData);
        
        // Si está habilitado CouponPasses, usar el callback específico
        if (formData.coupon_passes_enabled && onCreatedWithCoupons) {
          const couponConfig = {
            specificCodes: generatedCouponCodes, // Pasar los códigos específicos generados
            maxUsesPerCode: formData.coupon_uses_per_code || 3
          };
          onCreatedWithCoupons(contestData, couponConfig);
        }
      } else {
        void appDialog.alert('Concurso creado exitosamente');
      }
    }
  };

  const renderStepIndicator = () => (
    <Box sx={{ mb: 4 }}>
      {isMobile ? (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: 2,
          overflowX: 'auto'
        }}>
          {steps.slice(0, 4).map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <Box
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 }
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isCompleted ? 'success.main' : isActive ? 'primary.main' : 'grey.300',
                    color: isCompleted || isActive ? 'white' : 'grey.600',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <StepIcon />
                </Box>
                <Typography variant="caption" sx={{ fontWeight: isActive ? 600 : 400, fontSize: '0.7rem', textAlign: 'center' }}>
                  {step.name}
                </Typography>
              </Box>
            );
          })}
          {steps.length > 4 && steps.slice(4).map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <Box
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 }
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isCompleted ? 'success.main' : isActive ? 'primary.main' : 'grey.300',
                    color: isCompleted || isActive ? 'white' : 'grey.600',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <StepIcon />
                </Box>
                <Typography variant="caption" sx={{ fontWeight: isActive ? 600 : 400, fontSize: '0.7rem', textAlign: 'center' }}>
                  {step.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Stepper activeStep={currentStep - 1} alternativeLabel>
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <Step key={step.id}>
                <StepLabel
                  onClick={() => setCurrentStep(step.id)}
                  StepIconComponent={({ active, completed }) => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                        color: completed || active ? 'white' : 'grey.600',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <StepIcon />
                    </Box>
                  )}
                  sx={{ cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
                >
                  <Typography variant="caption" sx={{ fontWeight: isActive ? 600 : 400 }}>
                    {step.name}
                  </Typography>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      )}
    </Box>
  );

  const renderStep1 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 2.5 }}>
      <Alert 
        severity="info" 
        icon={<InfoIcon />}
        sx={{ 
          backgroundColor: '#e3f2fd',
          '& .MuiAlert-icon': { color: '#1976d2' }
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {t('basicinfo.basicinfo')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#1565c0' }}>
          {t('basicinfo.basicinfo_subtitle')}
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
        <Box
          sx={{
            width: isMobile ? '100%' : 112,
            height: isMobile ? 150 : 112,
            borderRadius: '22px',
            border: '1px solid #E7E5EA',
            overflow: 'hidden',
            flexShrink: 0,
            background: coverImagePreviewUrl
              ? '#f3f4f6'
              : 'linear-gradient(135deg, rgba(237,23,144,.12), rgba(48,111,200,.12))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ED1790'
          }}
        >
          {coverImagePreviewUrl ? (
            <Box
              component="img"
              src={coverImagePreviewUrl}
              alt="Contest cover"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <CameraIcon />
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: isMobile ? '100%' : 'auto' }}>
          <Typography variant="caption" sx={{ color: '#6B6F7B', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase' }}>
            Cover image
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              component="label"
              disabled={uploadingCover}
              sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 600 }}
            >
              {uploadingCover ? 'Uploading…' : formData.cover_image ? 'Change image' : 'Upload image'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    setUploadingCover(true);
                    const res = await uploadImageToS3(file);
                    const storedValue =
                      res.key ||
                      extractS3Key(res.contentUrl || res.s3Url || '') ||
                      res.contentUrl ||
                      res.sizes?.medium?.contentUrl ||
                      '';
                    if (storedValue) updateFormData('cover_image', storedValue);
                  } catch (err) {
                    console.error('Error subiendo cover_image:', err);
                    void appDialog.alert((err as Error).message || 'Error subiendo imagen');
                  } finally {
                    setUploadingCover(false);
                    e.target.value = '';
                  }
                }}
              />
            </Button>
            {formData.cover_image && (
              <Button
                variant="text"
                size="small"
                onClick={() => updateFormData('cover_image', '')}
                sx={{ color: '#6B6F7B', textTransform: 'none' }}
              >
                Remove
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <TextField
        fullWidth
        label={t('basicinfo.name')}
        required
          value={formData.name}
          onChange={(e) => updateFormData('name', e.target.value)}
          placeholder={t('basicinfo.name_placeholder')}
        error={!!errors.name}
        helperText={errors.name}
        variant="outlined"
      />

      <TextField
        fullWidth
        label={t('basicinfo.description')}
        required
        multiline
        rows={3}
          value={formData.description}
          onChange={(e) => updateFormData('description', e.target.value)}
          placeholder={t('basicinfo.description_placeholder')}
        error={!!errors.description}
        helperText={errors.description}
        variant="outlined"
      />

      <Box sx={{ display: 'flex', gap: 2, alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <TextField
            fullWidth
            label={t('basicinfo.theme_name')}
            value={formData.theme_name}
            onChange={(e) => updateFormData('theme_name', e.target.value)}
            placeholder={t('basicinfo.theme_name_placeholder')}
            variant="outlined"
          />
          <Tooltip 
            title={t('Tooltips_explications.theme_name')}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: '0.95rem',
                  maxWidth: 300,
                  textAlign: 'center'
                }
              }
            }}
          >
            <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
          </Tooltip>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.seasonal_theme}
              onChange={(e) => updateFormData('seasonal_theme', e.target.checked)}
              color="primary"
            />
          }
          label={t('basicinfo.seasonal_theme')}
        />
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t('basicinfo.theme_keywords')}
          </Typography>
          <Tooltip 
            title={t('Tooltips_explications.theme_keywords')}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: '0.95rem',
                  maxWidth: 300,
                  textAlign: 'center'
                }
              }
            }}
          >
            <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            fullWidth
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addKeyword();
              }
            }}
            placeholder={t('basicinfo.theme_keywords_placeholder')}
            variant="outlined"
            size="small"
          />
          <IconButton 
            onClick={addKeyword}
            color="primary"
            sx={{ 
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': { backgroundColor: 'primary.dark' }
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {formData.theme_keywords.map(keyword => (
            <Chip
              key={keyword}
              label={keyword}
              onDelete={() => removeKeyword(keyword)}
              deleteIcon={<CloseIcon />}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      </Box>

      <Card sx={{ backgroundColor: '#fbfcff' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DollarIcon />
          {t('basicinfo.prices_configuration')}
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TextField
                fullWidth
                label={t('basicinfo.entrance_fee')}
                required
                type="number"
                inputProps={{ step: 0.01, min: 0 }}
                value={formData.entrance_fee}
                onChange={(e) => updateFormData('entrance_fee', parseFloat(e.target.value) || 0)}
                error={!!errors.entrance_fee}
                helperText={errors.entrance_fee}
                variant="outlined"
                size="small"
              />
              <Tooltip 
                title={t('Tooltips_explications.prices_configuration')}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '0.95rem',
                      maxWidth: 300,
                      textAlign: 'center'
                    }
                  }
                }}
              >
                <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TextField
                fullWidth
                label={t('basicinfo.organizer_buy_rate')}
                type="number"
                inputProps={{ step: 0.01, min: 0 }}
                value={formData.organizer_buy_rate}
                onChange={(e) => updateFormData('organizer_buy_rate', parseFloat(e.target.value) || 0)}
                variant="outlined"
                size="small"
              />
              <Tooltip 
                title={t('Tooltips_explications.organizer_buy_rate')}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '0.95rem',
                      maxWidth: 300,
                      textAlign: 'center'
                    }
                  }
                }}
              >
                <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TextField
                fullWidth
                label={t('basicinfo.min_participants')}
                required
                type="number"
                inputProps={{ min: 1 }}
                value={formData.min_participants}
                onChange={(e) => updateFormData('min_participants', parseInt(e.target.value) || 24)}
                error={!!errors.min_participants}
                helperText={errors.min_participants}
                variant="outlined"
                size="small"
              />
              <Tooltip 
                title={t('Tooltips_explications.min_participants')}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '0.95rem',
                      maxWidth: 300,
                      textAlign: 'center'
                    }
                  }
                }}
              >
                <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TextField
                fullWidth
                label={t('basicinfo.max_participants')}
                required
                type="number"
                inputProps={{ min: formData.min_participants }}
                value={formData.max_participants}
                onChange={(e) => updateFormData('max_participants', parseInt(e.target.value) || 100)}
                error={!!errors.max_participants}
                helperText={errors.max_participants}
                variant="outlined"
                size="small"
              />
              <Tooltip 
                title={t('basicinfo.max_participants_required')}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '0.95rem',
                      maxWidth: 300,
                      textAlign: 'center'
                    }
                  }
                }}
              >
                <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ backgroundColor: '#ffffff' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon />
          {t('basicinfo.access_restrictions')}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
            {t('Tooltips_explications.eligibility_criteria')}
          </Typography>
          
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <RadioGroup
              value={formData.members_only && !formData.subscribers_only ? 'members' : formData.subscribers_only ? 'subscribers' : formData.ticket_based_only ? 'ticket_based' : ''}
              onChange={(e) => {
                if (e.target.value === 'members') {
                  updateFormData('members_only', true);
                  updateFormData('subscribers_only', false);
                  updateFormData('ticket_based_only', false);
                } else if (e.target.value === 'subscribers') {
                  updateFormData('subscribers_only', true);
                  updateFormData('members_only', false);
                  updateFormData('ticket_based_only', false);
                } else if (e.target.value === 'ticket_based') {
                  updateFormData('ticket_based_only', true);
                  updateFormData('members_only', false);
                  updateFormData('subscribers_only', false);
                }
              }}
              sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}
            >
              <FormControlLabel
                value="members"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t('basicinfo.members_only')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('basicinfo.members_only_description')}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="subscribers"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t('basicinfo.subscribers_only')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('basicinfo.subscribers_only_description')}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="ticket_based"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t('basicinfo.Ticket_based_only')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('basicinfo.Ticket_based_only_description')}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>
    </Box>
  );

  const renderStep2 = () => {
    // Validación en vivo del Premio Secundario según las bandas: detecta si el
    // total no se puede repartir exactamente entre los lugares y sugiere el
    // siguiente total válido hacia arriba (p. ej. 133 -> 134).
    const secondaryValidation = validateSecondaryTotal(
      formData.secondary_prize,
      formData.secondary_prize_places
    );
    const secondaryFeasibleRange = getSecondaryDistributableRange(formData.secondary_prize_places);
    const secondaryExampleTotal =
      formData.secondary_prize_places > 0
        ? Math.round(((secondaryFeasibleRange.min + secondaryFeasibleRange.max) / 2) * 100) / 100
        : 0;
    const secondaryBandLabelKey: Record<SecondaryBandKey, string> = {
      alta: 'prizesinfo.secondary_band_alta',
      media: 'prizesinfo.secondary_band_media',
      baja: 'prizesinfo.secondary_band_baja',
      minima: 'prizesinfo.secondary_band_minima',
    };
    const applySuggestedSecondaryTotal = () => {
      if (secondaryValidation.suggested == null) return;
      setSecondaryPrizeWithDistribution(String(secondaryValidation.suggested));
    };

    return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 2.5 }}>
      <Alert 
        severity="success" 
        icon={<AwardIcon />}
        sx={{ 
          backgroundColor: '#e8f5e8',
          '& .MuiAlert-icon': { color: '#2e7d32' }
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
          {t('prizesinfo.prizes_structure')}
        </Typography>
      </Alert>

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
        {t('Tooltips_explications.prizes_structure')}
      </Typography>

      <Box sx={{ maxWidth: isMobile ? '100%' : 360 }}>
        <TextField
          fullWidth
          select
          label={t('prizesinfo.prize_type')}
          value={formData.prize_type}
          onChange={(e) => updateFormData('prize_type', e.target.value)}
          variant="outlined"
          SelectProps={{
            native: true,
          }}
        >
          <option value="gift_card">{t('prizesinfo.gift_card')}</option>
          <option value="cash">{t('prizesinfo.cash')}</option>
          <option value="vouchers">{t('prizesinfo.vouchers')}</option>
          <option value="mixed">{t('prizesinfo.mixed')}</option>
        </TextField>
      </Box>

      {sponsorshipData?.reward_contribution > 0 && (
        <Alert severity="info" icon={<DollarIcon />} sx={{ backgroundColor: '#e8f4fd' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('prizesinfo.sponsor_reward_applied')}
          </Typography>
          <Typography variant="body2">
            ${Number(sponsorshipData.reward_contribution).toFixed(2)} → {t('prizesinfo.grand_prize')}
          </Typography>
        </Alert>
      )}

      {/* GRAN PREMIO: el usuario elige cuántos lugares y llena cada monto a mano */}
      <Card sx={{ backgroundColor: '#faf5ff', border: '1px solid #e9d5ff' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#6b21a8' }}>
            {t('prizesinfo.grand_prize')}
          </Typography>

          {sponsorshipData?.reward_contribution > 0 && (
            <Alert severity="info" icon={<DollarIcon />} sx={{ backgroundColor: '#e8f4fd' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('prizesinfo.sponsor_reward_applied')}
              </Typography>
              <Typography variant="body2">
                ${Number(sponsorshipData.reward_contribution).toFixed(2)} → {t('prizesinfo.grand_prize')}
              </Typography>
            </Alert>
          )}

          <TextField
            label={t('prizesinfo.grand_prize')}
            type="text"
            inputProps={{ inputMode: 'decimal' }}
            value={getPriceText('main_prize', formData.main_prize)}
            onChange={(e) => {
              setMainPrizeWithDistribution(sanitizeMoney(e.target.value));
            }}
            onBlur={() => clearRawText('main_prize')}
            placeholder={t('prizesinfo.main_prize_placeholder')}
            helperText={t('prizesinfo.grand_prize_description')}
            variant="outlined"
            sx={{ maxWidth: isMobile ? '100%' : 360 }}
          />

          <TextField
            label={t('prizesinfo.grand_prize_places_label')}
            type="text"
            inputProps={{ inputMode: 'numeric' }}
            value={getPriceText('grand_prize_places', formData.grand_prize_places)}
            onChange={(e) => {
              const clean = sanitizeInt(e.target.value);
              setRawText('grand_prize_places', clean);
              if (clean !== '') setGrandPrizePlaces(parseInt(clean, 10));
            }}
            onBlur={() => {
              if ((priceText['grand_prize_places'] ?? '') === '') {
                setGrandPrizePlaces(formData.grand_prize_places || 1);
              }
              clearRawText('grand_prize_places');
            }}
            helperText={t('prizesinfo.grand_prize_places_helper')}
            variant="outlined"
            sx={{ maxWidth: isMobile ? '100%' : 360 }}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
            {Array.from({ length: Math.min(Math.max(formData.grand_prize_places, 1), 4) }, (_, index) => {
              const icon = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅';
              const labelKey = index === 0 ? 'prize_first_place' : index === 1 ? 'prize_second_place' : index === 2 ? 'prize_third_place' : 'prize_fourth_place';
              const fieldName = GRAND_PRIZE_FIELDS[index];
              const amount = Number(formData[fieldName] as number) || 0;
              return (
                <TextField
                  key={fieldName}
                  fullWidth
                  label={`${icon} ${t(`prizesinfo.${labelKey}`)}`}
                  type="text"
                  inputProps={{ inputMode: 'decimal' }}
                  value={getPriceText(fieldName as string, amount)}
                  onChange={(e) => {
                    const value = sanitizeMoney(e.target.value);
                    setRawText(fieldName as string, value);
                    setGrandPlaceAmount(index, value === '' ? 0 : parseFloat(value) || 0);
                  }}
                  onBlur={() => clearRawText(fieldName as string)}
                  onFocus={(e) => e.target.select()}
                  variant="outlined"
                />
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid #e9d5ff' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('prizesinfo.grand_prize_assigned')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#6b21a8' }}>
              ${sumGrandPrizePlaces(formData).toFixed(2)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* PREMIO SECUNDARIO: número de lugares independiente y montos manuales */}
      <Card sx={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#166534' }}>
            {t('prizesinfo.secondary_prize')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.875rem', lineHeight: 1.6 }}>
            {t('prizesinfo.secondary_prize_explanation')}
          </Typography>

          <TextField
            label={t('prizesinfo.secondary_prize')}
            type="text"
            inputProps={{ inputMode: 'decimal' }}
            value={getPriceText('secondary_prize', formData.secondary_prize)}
            onChange={(e) => {
              setSecondaryPrizeWithDistribution(sanitizeMoney(e.target.value));
            }}
            onBlur={() => clearRawText('secondary_prize')}
            placeholder={t('prizesinfo.secondary_prize_placeholder')}
            helperText={
              formData.secondary_prize_places > 0
                ? (t('prizesinfo.secondary_prize_range') || 'Rango válido para {places} lugares: {min} – {max}. Montos menores al máximo muestran premios decrecientes (#1 gana más).')
                    .replace(/\{places\}/g, String(formData.secondary_prize_places))
                    .replace(/\{min\}/g, `$${secondaryFeasibleRange.min.toFixed(2)}`)
                    .replace(/\{max\}/g, `$${secondaryFeasibleRange.max.toFixed(2)}`)
                : t('prizesinfo.secondary_prize_auto')
            }
            variant="outlined"
            sx={{ maxWidth: isMobile ? '100%' : 360 }}
          />

          <TextField
            label={t('prizesinfo.secondary_prize_places_label')}
            type="text"
            inputProps={{ inputMode: 'numeric' }}
            value={getPriceText('secondary_prize_places', formData.secondary_prize_places)}
            onChange={(e) => {
              const clean = sanitizeInt(e.target.value);
              setRawText('secondary_prize_places', clean);
              if (clean !== '') setSecondaryPrizePlaces(parseInt(clean, 10));
            }}
            onBlur={() => {
              if ((priceText['secondary_prize_places'] ?? '') === '') {
                setSecondaryPrizePlaces(formData.secondary_prize_places || 0);
              }
              clearRawText('secondary_prize_places');
            }}
            helperText={t('prizesinfo.secondary_prize_places_helper')}
            variant="outlined"
            sx={{ maxWidth: isMobile ? '100%' : 360 }}
          />

          {/* Validación en vivo: el total no cuadra con las bandas -> sugerir
              el siguiente total válido hacia arriba (p. ej. 133 -> 134). */}
          {!secondaryValidation.feasible && (
            <Alert
              severity="warning"
              action={
                secondaryValidation.suggested != null ? (
                  <Button color="inherit" size="small" onClick={applySuggestedSecondaryTotal}>
                    {(t('prizesinfo.secondary_apply_suggested') || 'Usar {amount}').replace(
                      '{amount}',
                      `$${secondaryValidation.suggested.toFixed(2)}`
                    )}
                  </Button>
                ) : undefined
              }
            >
              {secondaryValidation.reason === 'below_min'
                ? (t('prizesinfo.secondary_below_min') || 'El total no se puede repartir entre los lugares secundarios.')
                    .replace(/\{min\}/g, `$${secondaryValidation.min.toFixed(2)}`)
                    .replace(/\{max\}/g, `$${secondaryValidation.max.toFixed(2)}`)
                    .replace(/\{suggested\}/g, `$${(secondaryValidation.suggested ?? secondaryValidation.min).toFixed(2)}`)
                    .replace(/\{places\}/g, String(formData.secondary_prize_places))
                : (t('prizesinfo.secondary_above_max') || 'El total supera el máximo repartible.')
                    .replace(/\{min\}/g, `$${secondaryValidation.min.toFixed(2)}`)
                    .replace(/\{max\}/g, `$${secondaryValidation.max.toFixed(2)}`)
                    .replace(/\{places\}/g, String(formData.secondary_prize_places))
                    .replace(/\{example\}/g, `$${secondaryExampleTotal.toFixed(2)}`)}
            </Alert>
          )}

          {formData.secondary_prize_places > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
              {Array.from({ length: formData.secondary_prize_places }, (_, index) => {
                const placeKey = `prize_place_${5 + index}`;
                const amount = Number(formData.dynamic_prizes[placeKey]) || 0;
                const band = getSecondaryBandForPlace(index, formData.secondary_prize_places);
                const bandLabel = band ? t(secondaryBandLabelKey[band.key]) || band.label : '';
                const bandMin = band ? band.min : 0;
                const bandMax = band ? band.max : 0;
                return (
                  <TextField
                    key={placeKey}
                    fullWidth
                    label={`🏅 ${t('prizesinfo.secondary_prize')} #${index + 1}${bandLabel ? ` · ${bandLabel}` : ''}`}
                    type="text"
                    inputProps={{ inputMode: 'decimal' }}
                    value={getPriceText(placeKey, amount)}
                    onChange={(e) => {
                      const value = sanitizeMoney(e.target.value);
                      setRawText(placeKey, value);
                      setSecondaryPlaceAmount(placeKey, value === '' ? 0 : parseFloat(value) || 0);
                    }}
                    onBlur={() => {
                      const current = Number(formData.dynamic_prizes[placeKey]) || 0;
                      const clamped = clampSecondaryPrizeForPlace(
                        current,
                        index,
                        formData.secondary_prize_places
                      );
                      if (clamped !== current) setSecondaryPlaceAmount(placeKey, clamped);
                      clearRawText(placeKey);
                    }}
                    onFocus={(e) => e.target.select()}
                    helperText={`${bandLabel ? `${bandLabel}: ` : ''}$${bandMin.toFixed(2)} – $${bandMax.toFixed(2)}`}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid #bbf7d0' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('prizesinfo.secondary_prize_assigned')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#166534' }}>
              ${sumSecondaryPrizePool(formData).toFixed(2)}
            </Typography>
          </Box>
          {secondaryValidation.reason === 'above_max' &&
            sumSecondaryPrizePool(formData) < formData.secondary_prize - 0.01 && (
            <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block' }}>
              {(t('prizesinfo.secondary_assigned_less_than_entered') ||
                'Solo se pueden asignar {assigned} con {places} lugares (máx. {max}). Escribe {example} o menos para ver premios decrecientes.')
                .replace(/\{assigned\}/g, `$${sumSecondaryPrizePool(formData).toFixed(2)}`)
                .replace(/\{places\}/g, String(formData.secondary_prize_places))
                .replace(/\{max\}/g, `$${secondaryFeasibleRange.max.toFixed(2)}`)
                .replace(/\{example\}/g, `$${secondaryExampleTotal.toFixed(2)}`)}
            </Typography>
          )}
        </CardContent>
      </Card>

      {errors.prizes && (
        <Alert severity="error">
          {errors.prizes}
        </Alert>
      )}
      
      {errors.secondary_prize && (
        <Alert severity="error">
          {errors.secondary_prize}
        </Alert>
      )}
      
      {errors.main_prize && (
        <Alert severity="error">
          {errors.main_prize}
        </Alert>
      )}

      <Box sx={{ mb: 3 }} />

      <Card sx={{ backgroundColor: '#f5f5f5' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            {t('prizesinfo.total_prize_pool_breakdown')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">{t('prizesinfo.grand_prize')}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ${sumGrandPrizePlaces(formData).toFixed(2)}
            </Typography>
          </Box>
          {sumSecondaryPrizePool(formData) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">{t('prizesinfo.secondary_prize_pool')}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ${sumSecondaryPrizePool(formData).toFixed(2)}
              </Typography>
            </Box>
          )}
          {sponsorshipData?.reward_contribution > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">{t('details_contest.Reward_Contribution')}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>
                ${Number(sponsorshipData.reward_contribution).toFixed(2)}
              </Typography>
            </Box>
          )}
          {/* SB-002: monto pendiente por financiar = Gran Premio − aporte del
              patrocinador. Solo se muestra cuando hay un aporte registrado. */}
          {sponsorshipData?.reward_contribution > 0 && (() => {
            const grandTarget = sumGrandPrizePlaces(formData);
            const funded = Number(sponsorshipData.reward_contribution) || 0;
            const pending = Math.round((grandTarget - funded) * 100) / 100;
            return (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {t('prizesinfo.pending_to_fund') !== 'prizesinfo.pending_to_fund'
                    ? t('prizesinfo.pending_to_fund')
                    : 'Pendiente por financiar'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: pending > 0 ? 'warning.main' : 'success.main' }}
                >
                  {pending > 0 ? `$${pending.toFixed(2)}` : '✓ Totalmente financiado'}
                </Typography>
              </Box>
            );
          })()}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('prizesinfo.total_prize_pool')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
              ${calculateTotalPrizePool(formData).toFixed(2)}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
    );
  };

  const renderStep3 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert 
        severity="info" 
        icon={<CalendarIcon />}
        sx={{ 
          backgroundColor: '#f3e5f5',
          '& .MuiAlert-icon': { color: '#7b1fa2' },
          width: '100%'
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
          {t('datesinfo.contest_schedule')}
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <TextField
              fullWidth
              label={t('datesinfo.submission_start')}
              required
              type="datetime-local"
              value={formData.submission_start}
              onChange={(e) => updateFormData('submission_start', e.target.value)}
              error={!!errors.submission_start}
              helperText={errors.submission_start}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip 
              title={t('Tooltips_explications.submission_start')}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: '0.95rem',
                    maxWidth: 300,
                    textAlign: 'center'
                  }
                }
              }}
            >
              <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <TextField
              fullWidth
              label={t('datesinfo.submission_deadline')}
              required
              type="datetime-local"
              value={formData.submission_deadline}
              onChange={(e) => updateFormData('submission_deadline', e.target.value)}
              error={!!errors.submission_deadline}
              helperText={errors.submission_deadline}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip 
              title={t('Tooltips_explications.submission_deadline')}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: '0.95rem',
                    maxWidth: 300,
                    textAlign: 'center'
                  }
                }
              }}
            >
              <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <TextField
              fullWidth
              label={t('datesinfo.voting_start')}
              type="datetime-local"
              value={formData.voting_start}
              onChange={(e) => updateFormData('voting_start', e.target.value)}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip 
              title={t('Tooltips_explications.voting_start')}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: '0.95rem',
                    maxWidth: 300,
                    textAlign: 'center'
                  }
                }
              }}
            >
              <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <TextField
              fullWidth
              label={t('datesinfo.voting_end')}
              type="datetime-local"
              value={formData.voting_end}
              onChange={(e) => updateFormData('voting_end', e.target.value)}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip 
              title={t('Tooltips_explications.voting_end')}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: '0.95rem',
                    maxWidth: 300,
                    textAlign: 'center'
                  }
                }
              }}
            >
              <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <TextField
            fullWidth
            label={t('datesinfo.winner_announcement')}
            required
            type="datetime-local"
            value={formData.winner_announcement}
            onChange={(e) => updateFormData('winner_announcement', e.target.value)}
            error={!!errors.winner_announcement}
            helperText={errors.winner_announcement}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
          <Tooltip 
            title={t('Tooltips_explications.winner_announcement')}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: '0.95rem',
                  maxWidth: 300,
                  textAlign: 'center'
                }
              }
            }}
          >
            <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  const handleMediaTypeChange = (mediaType: AllowedMediaType) => {
    const defaults = getMediaDefaultsForType(mediaType);
    setFormData((prev) => ({
      ...prev,
      allowed_media_types: mediaType,
      max_images: defaults.max_images,
      max_videos: defaults.max_videos,
      allowed_formats: defaults.allowed_formats,
      max_image_size_mb: defaults.max_image_size_mb,
      max_video_size_mb: defaults.max_video_size_mb,
      max_video_duration_seconds: defaults.max_video_duration_seconds,
    }));
  };

  const toggleAllowedFormat = (format: string) => {
    setFormData((prev) => {
      const normalized = format.toLowerCase();
      const current = prev.allowed_formats.map((item) => item.toLowerCase());
      const next = current.includes(normalized)
        ? current.filter((item) => item !== normalized)
        : [...current, normalized];
      return { ...prev, allowed_formats: next };
    });
  };

  const renderStep4 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert 
        severity="info" 
        icon={<CameraIcon />}
        sx={{ 
          backgroundColor: '#e8eaf6',
          '& .MuiAlert-icon': { color: '#3f51b5' }
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#3f51b5' }}>
          {t('mediainfo.media_requirements')}
        </Typography>
      </Alert>

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
        {t('Tooltips_explications.media_requirements')}
      </Typography>

      <TextField
        fullWidth
        select
        label={t('mediainfo.allowed_media_types')}
        value={formData.allowed_media_types}
        onChange={(e) => handleMediaTypeChange(e.target.value as AllowedMediaType)}
        variant="outlined"
        SelectProps={{
          native: true,
        }}
      >
        <option value="image">{t('mediainfo.only_images')}</option>
        <option value="video">{t('mediainfo.only_videos')}</option>
        <option value="both">{t('mediainfo.images_and_videos')}</option>
      </TextField>

      <Alert severity="info" sx={{ backgroundColor: '#eef2ff' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t('mediainfo.one_media_per_entry')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formData.allowed_media_types === 'both'
            ? t('mediainfo.one_media_per_entry_both')
            : formData.allowed_media_types === 'image'
              ? t('mediainfo.one_image_per_entry')
              : t('mediainfo.one_video_per_entry')}
        </Typography>
      </Alert>

      {(formData.allowed_media_types === 'image' || formData.allowed_media_types === 'both') && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('mediainfo.images_configuration')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('mediainfo.standard_image_preset', {
                width: STANDARD_IMAGE.width,
                height: STANDARD_IMAGE.height,
                ratio: STANDARD_IMAGE.aspectRatio,
                size: formData.max_image_size_mb,
              })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                label={t('mediainfo.max_images')}
                type="number"
                value={1}
                disabled
                helperText={t('mediainfo.fixed_one_per_entry')}
                variant="outlined"
                size="small"
                sx={{ flex: '1 1 180px' }}
              />
              <TextField
                fullWidth
                label={t('mediainfo.max_image_size_mb')}
                type="number"
                inputProps={{ step: 0.1, min: 0.1 }}
                value={formData.max_image_size_mb}
                onChange={(e) => updateFormData('max_image_size_mb', parseFloat(e.target.value) || STANDARD_IMAGE.maxSizeMb)}
                variant="outlined"
                size="small"
                sx={{ flex: '1 1 180px' }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {(formData.allowed_media_types === 'video' || formData.allowed_media_types === 'both') && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('mediainfo.videos_configuration')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('mediainfo.standard_video_preset', {
                width: STANDARD_VIDEO.width,
                height: STANDARD_VIDEO.height,
                ratio: STANDARD_VIDEO.aspectRatio,
                size: formData.max_video_size_mb,
                duration: formData.max_video_duration_seconds,
              })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                label={t('mediainfo.max_videos')}
                type="number"
                value={1}
                disabled
                helperText={t('mediainfo.fixed_one_per_entry')}
                variant="outlined"
                size="small"
                sx={{ flex: '1 1 160px' }}
              />
              <TextField
                fullWidth
                label={t('mediainfo.max_video_size_mb')}
                type="number"
                inputProps={{ step: 1, min: 1 }}
                value={formData.max_video_size_mb}
                onChange={(e) => updateFormData('max_video_size_mb', parseFloat(e.target.value) || STANDARD_VIDEO.maxSizeMb)}
                variant="outlined"
                size="small"
                sx={{ flex: '1 1 160px' }}
              />
              <TextField
                fullWidth
                label={t('mediainfo.max_video_duration_seconds')}
                type="number"
                inputProps={{ min: 10 }}
                value={formData.max_video_duration_seconds}
                onChange={(e) => updateFormData('max_video_duration_seconds', parseInt(e.target.value, 10) || STANDARD_VIDEO.maxDurationSeconds)}
                variant="outlined"
                size="small"
                sx={{ flex: '1 1 160px' }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('mediainfo.allowed_formats')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('mediainfo.allowed_formats_hint')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[...STANDARD_IMAGE.formats, ...STANDARD_VIDEO.formats]
              .filter((format, index, list) => list.indexOf(format) === index)
              .filter((format) => {
                const mediaType = formData.allowed_media_types as AllowedMediaType;
                if (mediaType === 'image') return (STANDARD_IMAGE.formats as readonly string[]).includes(format);
                if (mediaType === 'video') return (STANDARD_VIDEO.formats as readonly string[]).includes(format);
                return true;
              })
              .map((format) => (
                <Chip
                  key={format}
                  label={format.toUpperCase()}
                  clickable
                  color={formData.allowed_formats.map((f) => f.toLowerCase()).includes(format) ? 'primary' : 'default'}
                  onClick={() => toggleAllowedFormat(format)}
                  variant={formData.allowed_formats.map((f) => f.toLowerCase()).includes(format) ? 'filled' : 'outlined'}
                />
              ))}
          </Box>
          {errors.allowed_formats && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {errors.allowed_formats}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2}}>
            <Typography variant="subtitle2" sx={{ mr: 1, fontSize: '1.1rem', fontWeight: 500 }}>
              {t('mediainfo.photo_rewards')}
            </Typography>
            <Tooltip 
              title={t('Tooltips_explications.photo_rewards_mode')}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: '0.95rem',
                    maxWidth: 300,
                    textAlign: 'center'
                  }
                }
              }}
            >
              <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem' }} />
            </Tooltip>
          </Box>

          <TextField
            fullWidth
            select
            label={t('mediainfo.photo_rewards_mode')}
            value={formData.photo_rewards_mode}
            onChange={(e) => updateFormData('photo_rewards_mode', e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
            SelectProps={{
              native: true,
            }}
          >
            <option value="disabled">{t('mediainfo.disabled')}</option>
            <option value="optional">{t('mediainfo.optional')}</option>
            <option value="required">{t('mediainfo.required')}</option>
          </TextField>

        {formData.photo_rewards_mode !== 'disabled' && (
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <TextField
                  fullWidth
                  label={<span>Photo Rewards<br/>User Percentage</span>}
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  value={formData.photo_rewards_user_percentage}
                  onChange={(e) => updateFormData('photo_rewards_user_percentage', parseFloat(e.target.value) || 80)}
                  variant="outlined"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{ 
                    '& .MuiInputBase-root': {
                      minHeight: '80px',
                      paddingTop: '8px',
                      paddingBottom: '8px'
                    },
                    '& .MuiInputLabel-root': {
                      transform: 'translate(14px, 20px) scale(1)',
                      '&.Mui-focused, &.MuiFormLabel-filled': {
                        transform: 'translate(14px, -9px) scale(0.75)'
                      }
                    }
                  }}
                />
              <Tooltip 
                title={t('Tooltips_explications.photo_rewards_user_percentage')}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '0.95rem',
                      maxWidth: 300,
                      textAlign: 'center'
                    }
                  }
                }}
              >
                  <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
                </Tooltip>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <TextField
                  fullWidth
                  label={<span>Photo Rewards<br/>Organizer Percentage</span>}
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  value={formData.photo_rewards_organizer_percentage}
                  onChange={(e) => updateFormData('photo_rewards_organizer_percentage', parseFloat(e.target.value) || 10)}
                  variant="outlined"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{ 
                    '& .MuiInputBase-root': {
                      minHeight: '80px',
                      paddingTop: '8px',
                      paddingBottom: '8px'
                    },
                    '& .MuiInputLabel-root': {
                      transform: 'translate(14px, 20px) scale(1)',
                      '&.Mui-focused, &.MuiFormLabel-filled': {
                        transform: 'translate(14px, -9px) scale(0.75)'
                      }
                    }
                  }}
                />
              <Tooltip 
                title={t('Tooltips_explications.photo_rewards_organizer_percentage')}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '0.95rem',
                      maxWidth: 300,
                      textAlign: 'center'
                    }
                  }
                }}
              >
                  <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
                </Tooltip>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <TextField
                  fullWidth
                  label={<span>Photo Rewards<br/>Noah's Ark Percentage</span>}
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  value={formData.photo_rewards_noahs_ark_percentage}
                  onChange={(e) => updateFormData('photo_rewards_noahs_ark_percentage', parseFloat(e.target.value) || 10)}
                  variant="outlined"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{ 
                    '& .MuiInputBase-root': {
                      minHeight: '80px',
                      paddingTop: '8px',
                      paddingBottom: '8px'
                    },
                    '& .MuiInputLabel-root': {
                      transform: 'translate(14px, 20px) scale(1)',
                      '&.Mui-focused, &.MuiFormLabel-filled': {
                        transform: 'translate(14px, -9px) scale(0.75)'
                      }
                    }
                  }}
                />
              <Tooltip 
                title={t('Tooltips_explications.photo_rewards_noahs_ark_percentage')}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '0.95rem',
                      maxWidth: 300,
                      textAlign: 'center'
                    }
                  }
                }}
              >
                  <InfoIcon color="primary" sx={{ cursor: 'help', fontSize: '1.2rem', ml: 1 }} />
                </Tooltip>
              </Box>
            </Box>
        )}
        </CardContent>
      </Card>
      
      <Box sx={{ height: '20px' }} />
    </Box>
  );

  const renderStep5 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert 
        severity="warning" 
        icon={<GlobeIcon />}
        sx={{ 
          backgroundColor: '#fff3e0',
          '& .MuiAlert-icon': { color: '#f57c00' },
          width: '100%'
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#f57c00' }}>
          {t('eligibilityinfo.eligibility_criteria')}
        </Typography>
      </Alert>

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
        {t('eligibilityinfo.eligibility_criteria_description')}
      </Typography>

      <TextField
        fullWidth
        select
        label={t('eligibilityinfo.allowed_countries')}
        value={sanitizeAllowedCountries(formData.allowed_countries)}
        onChange={(e) => {
          const value =
            typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          updateFormData(
            'allowed_countries',
            normalizeCountrySelection(formData.allowed_countries, value as string[])
          );
        }}
        error={Boolean(errors.allowed_countries)}
        helperText={errors.allowed_countries || undefined}
        variant="outlined"
        SelectProps={{
          multiple: true,
          renderValue: (selected) => formatAllowedCountriesLabel(selected as string[], t),
        }}
      >
        <MenuItem value={ALL_COUNTRIES_VALUE}>{t('eligibilityinfo.all_countries')}</MenuItem>
        {CONTEST_COUNTRY_REGION_ORDER.flatMap((region) => [
          <ListSubheader key={`region-${region}`}>{t(`eligibilityinfo.region_${region}`)}</ListSubheader>,
          ...getCountriesByRegion(region).map((country) => (
            <MenuItem key={country.code} value={country.code}>
              {t(`eligibilityinfo.${country.code}`)}
            </MenuItem>
          )),
        ])}
      </TextField>

      {isAllCountriesSelection(formData.allowed_countries) ? (
        <Alert severity="success" sx={{ backgroundColor: '#ecfdf5' }}>
          <Typography variant="body2">
            {t('eligibilityinfo.all_countries_description', {
              count: getAllConfiguredCountryCodes().length,
            })}
          </Typography>
        </Alert>
      ) : (
        <Alert severity="info">
          <Typography variant="body2">
            {t('eligibilityinfo.countries_selected_count', {
              count: sanitizeAllowedCountries(formData.allowed_countries).length,
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {formatAllowedCountriesLabel(formData.allowed_countries, t)}
          </Typography>
        </Alert>
      )}

      <TextField
        fullWidth
        select
        label={t('eligibilityinfo.allowed_pet_types')}
        value={sanitizeAllowedPetTypes(formData.allowed_pet_types)}
        onChange={(e) => {
          const value =
            typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          updateFormData(
            'allowed_pet_types',
            normalizePetTypeSelection(formData.allowed_pet_types, value as string[])
          );
        }}
        error={Boolean(errors.allowed_pet_types)}
        helperText={errors.allowed_pet_types || undefined}
        variant="outlined"
        SelectProps={{
          multiple: true,
          renderValue: (selected) => formatAllowedPetTypesLabel(selected as string[], t),
        }}
      >
        <MenuItem value={ALL_PET_TYPES_VALUE}>{t('eligibilityinfo.all_pet_types')}</MenuItem>
        {CONTEST_PET_GROUP_ORDER.flatMap((group) => [
          <ListSubheader key={`pet-group-${group}`}>{t(`eligibilityinfo.pet_group_${group}`)}</ListSubheader>,
          ...getPetTypesByGroup(group).map((pet) => (
            <MenuItem key={pet.code} value={pet.code}>
              {t(`eligibilityinfo.${pet.code}`)}
            </MenuItem>
          )),
        ])}
      </TextField>

      {isAllPetTypesSelection(formData.allowed_pet_types) ? (
        <Alert severity="success" sx={{ backgroundColor: '#ecfdf5' }}>
          <Typography variant="body2">
            {t('eligibilityinfo.all_pet_types_description', {
              count: getAllConfiguredPetTypeCodes().length,
            })}
          </Typography>
        </Alert>
      ) : (
        <Alert severity="info">
          <Typography variant="body2">
            {t('eligibilityinfo.pet_types_selected_count', {
              count: sanitizeAllowedPetTypes(formData.allowed_pet_types).length,
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {formatAllowedPetTypesLabel(formData.allowed_pet_types, t)}
          </Typography>
        </Alert>
      )}

      <TextField
        fullWidth
        select
        label={t('eligibilityinfo.age_group')}
        value={formData.age_group}
        onChange={(e) => {
          const value = e.target.value;
          updateFormData('age_group', value);
          if (value === 'under_18') {
            updateFormData('requires_guardian_consent', true);
          }
        }}
        variant="outlined"
        SelectProps={{
          native: true,
        }}
      >
        <option value="all_ages">{t('eligibilityinfo.all_ages')}</option>
        <option value="under_18">{t('eligibilityinfo.under_18')}</option>
        <option value="over_18">{t('eligibilityinfo.over_18')}</option>
      </TextField>

      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t('eligibilityinfo.additional_eligibility')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('eligibilityinfo.additional_eligibility_description')}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.requires_guardian_consent}
                onChange={(e) => updateFormData('requires_guardian_consent', e.target.checked)}
              />
            }
            label={t('eligibilityinfo.guardian_consent_required')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, ml: 4 }}>
            {t('eligibilityinfo.guardian_consent_helper')}
          </Typography>

          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('eligibilityinfo.custom_age_range')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label={t('eligibilityinfo.min_age')}
              type="number"
              inputProps={{ min: 0, max: 120 }}
              value={formData.min_age ?? ''}
              onChange={(e) =>
                updateFormData('min_age', e.target.value === '' ? null : parseInt(e.target.value, 10))
              }
              helperText={t('eligibilityinfo.min_age_helper')}
              variant="outlined"
              size="small"
              sx={{ flex: '1 1 160px' }}
            />
            <TextField
              label={t('eligibilityinfo.max_age')}
              type="number"
              inputProps={{ min: 0, max: 120 }}
              value={formData.max_age ?? ''}
              onChange={(e) =>
                updateFormData('max_age', e.target.value === '' ? null : parseInt(e.target.value, 10))
              }
              error={Boolean(errors.max_age)}
              helperText={errors.max_age || t('eligibilityinfo.max_age_helper')}
              variant="outlined"
              size="small"
              sx={{ flex: '1 1 160px' }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const renderStep6 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert 
        severity="info" 
        icon={<SchoolIcon />}
        sx={{ 
          backgroundColor: '#f5f5f5',
          '& .MuiAlert-icon': { color: '#616161' },
          width: '100%'
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#616161' }}>
          {t('advancedinfo.advanced_settings')}
        </Typography>
      </Alert>

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
        {t('advancedinfo.advanced_settings_description')}
      </Typography>

      <TextField
        fullWidth
        select
        label={t('advancedinfo.education_type')} 
        value={formData.education_type}
        onChange={(e) => updateFormData('education_type', e.target.value)}
        variant="outlined"
        SelectProps={{
          native: true,
        }}
      >
        <option value="none">{t('advancedinfo.none')}</option>
        <option value="elementary_school">{t('advancedinfo.elementary_school')}</option>
        <option value="high_school">{t('advancedinfo.high_school')}</option>
        <option value="college_university">{t('advancedinfo.college_university')}</option>
      </TextField>

      {formData.education_type !== 'none' && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {t('eligibilityinfo.education_eligibility')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label={t('eligibilityinfo.institution_name')}
                value={formData.institution_name}
                onChange={(e) => updateFormData('institution_name', e.target.value)}
                variant="outlined"
              />
              <TextField
                fullWidth
                label={t('eligibilityinfo.academic_subject')}
                value={formData.academic_subject}
                onChange={(e) => updateFormData('academic_subject', e.target.value)}
                variant="outlined"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.requires_institution_verification}
                    onChange={(e) =>
                      updateFormData('requires_institution_verification', e.target.checked)
                    }
                  />
                }
                label={t('eligibilityinfo.requires_institution_verification')}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      <TextField
        fullWidth
        select
        label={t('advancedinfo.sponsorship_type')}
        value={formData.sponsorship_type}
        onChange={(e) => updateFormData('sponsorship_type', e.target.value)}
        variant="outlined"
        SelectProps={{
          native: true,
        }}
        disabled={loadingSponsorshipTypeOptions}
        helperText={loadingSponsorshipTypeOptions ? t('platforms.loading') : undefined}
      >
        <option value={SPONSORSHIP_TYPE_NONE}>{t('advancedinfo.none_sponsorship')}</option>
        {sponsorshipTypeOptions.map((type) => (
          <option key={type.value} value={type.value}>
            {type.name}
          </option>
        ))}
      </TextField>

      <TextField
        fullWidth
        select
        label={t('advancedinfo.vote_type')}
        value={formData.vote_type}
        onChange={(e) => updateFormData('vote_type', e.target.value)}
        variant="outlined"
        SelectProps={{
          native: true,
        }}
      >
        <option value="users">{t('advancedinfo.vote_type_users')}</option>
        <option value="judges">{t('advancedinfo.vote_type_judges')}</option>
      </TextField>

      {formData.vote_type === 'judges' && (
        <TextField
          fullWidth
          label={t('advancedinfo.size_vote')}
          type="number"
          inputProps={{ step: 0.1, min: 0.1 }}
          value={formData.size_vote}
          onChange={(e) => updateFormData('size_vote', parseFloat(e.target.value) || 1)}
          variant="outlined"
          helperText={t('advancedinfo.size_vote_helper')}
        />
      )}

      {/* ── Winner Modal Customization ── */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            🏆 Winner Modal Customization
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customize the title, subtitle, body text and image shown in the winner popup inside the app. Leave blank to use the defaults.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Modal Title"
              placeholder="e.g. 🎉 Contest Winner!!"
              value={formData.winner_modal_title}
              onChange={(e) => updateFormData('winner_modal_title', e.target.value)}
              variant="outlined"
              inputProps={{ maxLength: 80 }}
            />
            <TextField
              fullWidth
              label="Modal Subtitle"
              placeholder="e.g. You've won a prize!"
              value={formData.winner_modal_subtitle}
              onChange={(e) => updateFormData('winner_modal_subtitle', e.target.value)}
              variant="outlined"
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Modal Body Text"
              placeholder="e.g. Congratulations! Your prize will be processed within 24-48 hours."
              value={formData.winner_modal_body_text}
              onChange={(e) => updateFormData('winner_modal_body_text', e.target.value)}
              variant="outlined"
              inputProps={{ maxLength: 400 }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Modal Image (replaces default badge image)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 600 }}
                >
                  {formData.winner_modal_image ? 'Change image' : 'Upload image'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const res = await uploadImageToS3(file);
                        const storedValue =
                          res.key ||
                          extractS3Key(res.contentUrl || res.s3Url || '') ||
                          res.contentUrl ||
                          res.sizes?.medium?.contentUrl ||
                          '';
                        if (storedValue) updateFormData('winner_modal_image', storedValue);
                      } catch (err) {
                        console.error('Error uploading winner_modal_image:', err);
                        void appDialog.alert((err as Error).message || 'Error uploading image');
                      } finally {
                        e.target.value = '';
                      }
                    }}
                  />
                </Button>
                {formData.winner_modal_image && (
                  <>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => updateFormData('winner_modal_image', '')}
                      sx={{ color: '#6B6F7B', textTransform: 'none' }}
                    >
                      Remove
                    </Button>
                    <Box
                      component="img"
                      src={getCompleteImagePath(formData.winner_modal_image) || formData.winner_modal_image}
                      sx={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e0e0' }}
                    />
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ── Per-position Notification Templates ── */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            🔔 Winner Notification Messages (per position)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customize the push notification message sent to winners per place. Use <code>{'{contest_name}'}</code> as a placeholder. Leave blank for the default message.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Array.from({ length: formData.number_of_places || 4 }, (_, i) => i + 1).map((place) => (
              <TextField
                key={place}
                fullWidth
                label={`Place ${place} notification`}
                placeholder={`e.g. 🥇 Congrats! You won ${place === 1 ? '1st' : place === 2 ? '2nd' : place === 3 ? '3rd' : `${place}th`} place in {contest_name}!`}
                value={formData.notification_templates[String(place)] || ''}
                onChange={(e) => {
                  const updated = { ...formData.notification_templates };
                  if (e.target.value) {
                    updated[String(place)] = e.target.value;
                  } else {
                    delete updated[String(place)];
                  }
                  updateFormData('notification_templates', updated);
                }}
                variant="outlined"
                inputProps={{ maxLength: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {place === 1 ? '🥇' : place === 2 ? '🥈' : place === 3 ? '🥉' : `#${place}`}
                    </InputAdornment>
                  ),
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* ── Benefactores (opcional) ── */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            🤝 {t('benefactor.section_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('benefactor.section_description')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.benefactor_enabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  updateFormData('benefactor_enabled', on);
                  if (!on) updateFormData('benefactors', []);
                }}
              />
            }
            label={t('benefactor.enable_label')}
          />

          {formData.benefactor_enabled && (
            <Box sx={{ mt: 2 }}>
              {/* Seleccionar (uno o varios) de benefactores ya creados. Las
                  selecciones se muestran como chips dentro del propio campo. */}
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={benefactorOptions}
                value={formData.benefactors}
                loading={loadingSavedBenefactors}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(opt, val) => opt.name === val.name && opt.type === val.type}
                noOptionsText={loadingSavedBenefactors ? t('benefactor.loading') : t('benefactor.no_saved')}
                onChange={(_e, selected) => {
                  updateFormData('benefactors', selected as BenefactorItem[]);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((b, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    const icon =
                      b.type === 'shelter'
                        ? '🏠'
                        : b.category === 'cause'
                        ? '💛'
                        : b.category === 'rescue'
                        ? '🐾'
                        : b.category === 'foundation'
                        ? '🏛️'
                        : '🤝';
                    return (
                      <Chip
                        key={key}
                        label={`${icon} ${b.name}`}
                        {...tagProps}
                        sx={{ maxWidth: 280 }}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('benefactor.select_existing')}
                    helperText={t('benefactor.select_existing_help')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSavedBenefactors ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              {/* Agregar organización / causa nueva */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  label={t('benefactor.name_label')}
                  value={newBenefactorName}
                  onChange={(e) => { setNewBenefactorName(e.target.value); setNewBenefactorError(''); }}
                  error={!!newBenefactorError}
                  helperText={newBenefactorError || t('benefactor.name_help')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomBenefactor(newBenefactorName, newBenefactorCategory);
                    }
                  }}
                  sx={{ flex: '1 1 200px', minWidth: 180 }}
                  size="small"
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>{t('benefactor.category_label')}</InputLabel>
                  <Select
                    value={newBenefactorCategory}
                    label={t('benefactor.category_label')}
                    onChange={(e) => setNewBenefactorCategory(e.target.value as BenefactorItem['category'])}
                  >
                    {BENEFACTOR_CATEGORIES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>{t(c.labelKey)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => addCustomBenefactor(newBenefactorName, newBenefactorCategory)}
                  sx={{ mt: '4px', height: 40 }}
                >
                  {t('benefactor.add_btn')}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ backgroundColor: '#e3f2fd' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
            {t('advancedinfo.summary')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="primary">
                {t('advancedinfo.name')}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formData.name || 'Sin configurar'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="primary">
                {t('advancedinfo.entrance_fee')}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ${formData.entrance_fee.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="primary">
                {t('prizesinfo.total_prize_pool')}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ${calculateTotalPrizePool(formData).toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="primary">
                {t('prizesinfo.grand_prize')}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ${sumGrandPrizePlaces(formData).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const renderStep7 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert 
        severity="info" 
        icon={<InfoIcon />}
        sx={{ 
          backgroundColor: '#f3e5f5',
          '& .MuiAlert-icon': { color: '#7b1fa2' }
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
          {t('couponpassesinfo.coupon_passes')}
        </Typography>
      </Alert>

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
        {t('couponpassesinfo.coupon_passes_description')}
      </Typography>

      {existingContest?.id && (linkedCodesInfo.loading || linkedCodesInfo.pass.total > 0 || linkedCodesInfo.coupon.total > 0) && (
        <Box
          sx={{
            mb: 1,
            p: 2,
            borderRadius: 2,
            border: '1px solid #bfdbfe',
            backgroundColor: '#eff6ff'
          }}
        >
          {linkedCodesInfo.loading ? (
            <Typography variant="body2" sx={{ color: '#1e40af' }}>
              Cargando estado de códigos del concurso…
            </Typography>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.5 }}>
                Códigos ya enlazados a este concurso
              </Typography>
              <Typography variant="body2" sx={{ color: '#1e3a8a', mb: 1 }}>
                Este concurso ya tiene{' '}
                <b>{linkedCodesInfo.pass.total + linkedCodesInfo.coupon.total}</b> código(s) generados.
                {linkedCodesInfo.pass.total > 0 && (
                  <> {' '}<b>{linkedCodesInfo.pass.total}</b> Pase(s) (Pass Codes), normalmente generados desde Sponsorship
                    {(linkedCodesInfo.pass.printed > 0 || linkedCodesInfo.pass.digital > 0 || linkedCodesInfo.pass.na > 0) && (
                      <>{' '}— Impreso: {linkedCodesInfo.pass.printed}, Digital: {linkedCodesInfo.pass.digital}, NA: {linkedCodesInfo.pass.na}</>
                    )}.
                  </>
                )}
                {linkedCodesInfo.coupon.total > 0 && (
                  <> {' '}<b>{linkedCodesInfo.coupon.total}</b> Cupón(es) (CouponPass).</>
                )}
              </Typography>
              <Typography variant="caption" sx={{ color: '#3730a3', fontStyle: 'italic', display: 'block' }}>
                Nota: el selector de abajo solo controla el sistema de <b>Cupones (CouponPass)</b> dentro de Pet Contest.
                Los <b>Pases</b> que llegan desde Sponsorship se gestionan en "Passes &amp; Codes" y no dependen de este interruptor.
              </Typography>
            </>
          )}
        </Box>
      )}

      <TextField
        fullWidth
        select
        label={t('couponpassesinfo.enable_coupon_passes')}
        value={formData.coupon_passes_enabled ? 'enabled' : 'disabled'}
        onChange={(e) => updateFormData('coupon_passes_enabled', e.target.value === 'enabled')}
        variant="outlined"
        SelectProps={{
          native: true,
        }}
      >
        <option value="disabled">{t('couponpassesinfo.disabled')}</option>
        <option value="enabled">{t('couponpassesinfo.enabled')}</option>
      </TextField>

      {formData.coupon_passes_enabled && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Selector de tipos de pase a generar por defecto para el concurso.
              Coupon Pass = entradas gratis; Contest Pass = entradas con valor.
              VIP Pass y Judge Invite quedan reservados (no editables por ahora). */}
          <FormControl component="fieldset" sx={{ mb: 1 }}>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Tipos de pase a generar
            </FormLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {([
                { value: 'coupon_pass', label: 'Coupon Pass (entradas gratuitas)', disabled: false },
                { value: 'entry_ticket', label: 'Contest Pass (entradas con valor)', disabled: false },
                { value: 'vip_pass', label: 'VIP Pass (próximamente)', disabled: true },
                { value: 'judge_invite', label: 'Judge Invite (próximamente)', disabled: true },
              ] as Array<{ value: string; label: string; disabled: boolean }>).map((opt) => {
                const selected = Array.isArray(formData.pass_types) && formData.pass_types.includes(opt.value);
                return (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Checkbox
                        checked={!!selected}
                        disabled={opt.disabled}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const current = Array.isArray(formData.pass_types) ? formData.pass_types : [];
                          const next = checked
                            ? Array.from(new Set([...current, opt.value]))
                            : current.filter((t) => t !== opt.value);
                          updateFormData('pass_types', next);
                        }}
                      />
                    }
                    label={opt.label}
                  />
                );
              })}
            </Box>
            <FormHelperText>
              Selecciona qué tipos de entrada se generarán por defecto en Pet Contest. Se crearán sus
              códigos/QR sin quedar vinculados a ninguna propuesta hasta que PetFlix los relacione.
            </FormHelperText>
            {(!Array.isArray(formData.pass_types) ||
              !(formData.pass_types.includes('coupon_pass') || formData.pass_types.includes('entry_ticket'))) && (
              <FormHelperText error>
                Selecciona al menos Coupon Pass o Contest Pass.
              </FormHelperText>
            )}
          </FormControl>

          <TextField
            fullWidth
            label={t('couponpassesinfo.number_of_uses_per_coupon')}
            type="number"
            inputProps={{ min: 1, max: 10 }}
            value={formData.coupon_uses_per_code || 3}
            onChange={(e) => updateFormData('coupon_uses_per_code', parseInt(e.target.value) || 3)}
            variant="outlined"
            helperText={t('couponpassesinfo.number_of_uses_per_coupon')}
          />
          
          <TextField
            fullWidth
            label={t('couponpassesinfo.number_of_coupons_to_generate')}
            type="number"
            inputProps={{ min: 1, max: 1000 }}
            value={formData.coupon_quantity || 10}
            onChange={(e) => updateFormData('coupon_quantity', parseInt(e.target.value) || 10)}
            variant="outlined"
            helperText={t('couponpassesinfo.number_of_coupons_to_generate_description')}
          />

          {/* Campo de Máximo de Pass Codes removido por requerimiento */}

          {/* max_coupass y uses_per_coupon se derivan automáticamente de coupon_quantity y coupon_uses_per_code */}

          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {t('couponpassesinfo.integration_with_petflix_qr_tags')}
                  <Tooltip title={t('Tooltips_explications.integration_with_petflix_qr_tags')} arrow>
                    <InfoIcon sx={{ fontSize: 18, color: 'primary.main', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </FormLabel>
            <RadioGroup
              value={formData.petflix_qr_integration || 'disabled'}
              onChange={(e) => updateFormData('petflix_qr_integration', e.target.value as 'disabled' | 'enabled' | 'add_to_existing')}
            >
              <FormControlLabel
                value="disabled"
                control={<Radio />}
                label={t('couponpassesinfo.no_integration_with_qr_tags')}
              />
              <FormControlLabel
                value="enabled"
                control={<Radio />}
                label={t('couponpassesinfo.integrate_with_petflix_qr_tags')}
              />
              <FormControlLabel
                value="add_to_existing"
                control={<Radio />}
                label={t('couponpassesinfo.add_qr_to_existing_coupon_passes')}
              />
            </RadioGroup>
            </FormControl>
          </Box>

          {(formData.petflix_qr_integration === 'enabled' || formData.petflix_qr_integration === 'add_to_existing') && (
            <>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <InputLabel sx={{ position: 'static', transform: 'none', fontSize: '0.875rem' }}>
                    {t('couponpassesinfo.select_shelter')}
                  </InputLabel>
                  <Tooltip title={t('Tooltips_explications.select_shelter')} arrow>
                    <InfoIcon sx={{ fontSize: 18, color: 'primary.main', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <FormControl fullWidth>
                  <InputLabel>{t('couponpassesinfo.select_shelter')}</InputLabel>
                  <Select
                    value={(() => {
                      const currentValue = formData.petflix_qr_type || '';
                      // Validar que el valor esté en la lista de shelters disponibles
                      if (currentValue && shelters.length > 0) {
                        const isValid = shelters.some((shelter: any) => shelter._id === currentValue);
                        return isValid ? currentValue : '';
                      }
                      return '';
                    })()}
                    onChange={(e) => updateFormData('petflix_qr_type', e.target.value)}
                    label={t('couponpassesinfo.select_shelter')}
                    disabled={loadingShelters}
                  >
                    {loadingShelters ? (
                      <MenuItem value="" disabled>Loading shelters...</MenuItem>
                    ) : shelters.length === 0 ? (
                      <MenuItem value="" disabled>No shelters available</MenuItem>
                    ) : (
                      shelters.map((shelter) => (
                        <MenuItem key={shelter._id} value={shelter._id}>
                          {shelter.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <InputLabel sx={{ position: 'static', transform: 'none', fontSize: '0.875rem' }}>
                    Tag Type
                  </InputLabel>
                  <Tooltip title={t('Tooltips_explications.tag_type')} arrow>
                    <InfoIcon sx={{ fontSize: 18, color: 'primary.main', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <FormControl fullWidth>
                  <InputLabel>Tag Type</InputLabel>
                  <Select
                    value={formData.tagType || 'standard'}
                    onChange={(e) => updateFormData('tagType', e.target.value)}
                    label="Tag Type"
                  >
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="promotional">Promotional</MenuItem>
                    <MenuItem value="adoption_rescue">Adoption Rescue</MenuItem>
                    <MenuItem value="resale">Resale</MenuItem>
                    <MenuItem value="foster_rescue">Foster Rescue</MenuItem>
                    <MenuItem value="premium">Premium</MenuItem>
                    <MenuItem value="ambassador">Ambassador</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.individual_qr || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        updateFormData('individual_qr', checked);
                        if (!checked) {
                          updateFormData('create_new_qr_tags', false);
                          updateFormData('use_existing_codes', false);
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {t('couponpassesinfo.individual_qr')}
                      <Tooltip title={t('Tooltips_explications.individual_qr')} arrow>
                        <InfoIcon sx={{ fontSize: 18, color: 'primary.main', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  }
                />
                <FormHelperText sx={{ mb: 0.1 }}>
                  {t('couponpassesinfo.individual_qr_description')}
                </FormHelperText>
              </Box>

              <Box sx={{ mt: 0.1, ml: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.create_new_qr_tags || false}
                      disabled={!formData.individual_qr}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        updateFormData('create_new_qr_tags', checked);
                        if (checked) {
                          updateFormData('use_existing_codes', false);
                        }
                        // Resetear el conteo cuando se desmarca
                        if (!checked) {
                          setCouponPassesWithoutQRCount(null);
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {t('couponpassesinfo.create_new_qr_tags')}
                      <Tooltip title={t('Tooltips_explications.create_new_qr_tags')} arrow>
                        <InfoIcon sx={{ fontSize: 18, color: 'primary.main', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  }
                  sx={{ mb: 0, '& .MuiFormControlLabel-label': { mb: 0 } }}
                />
              </Box>

              {formData.create_new_qr_tags && formData.petflix_qr_integration === 'add_to_existing' && existingContest?.id && (
                <Box sx={{ mt: 1, ml: 4 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      try {
                        setLoadingCount(true);
                        const result = await couponService.countCouponPassesWithoutQR(
                          existingContest.id,
                          formData.individual_qr || false
                        );
                        setCouponPassesWithoutQRCount(result.count);
                      } catch (error) {
                        console.error('Error al contar coupon passes sin QR tag:', error);
                        void appDialog.alert('Error al contar coupon passes sin QR tag: ' + (error instanceof Error ? error.message : 'Error desconocido'));
                      } finally {
                        setLoadingCount(false);
                      }
                    }}
                    disabled={loadingCount || !existingContest?.id}
                    sx={{ mb: 1 }}
                  >
                    {loadingCount ? 'Contando...' : 'Ver cantidad de coupon passes sin QR tag'}
                  </Button>
                  {couponPassesWithoutQRCount !== null && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                      <strong>Coupon passes sin QR tag: {couponPassesWithoutQRCount}</strong>
                    </Typography>
                  )}
                </Box>
              )}


              <Box sx={{ mt: -1, ml: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.use_existing_codes || false}
                      disabled={!formData.individual_qr}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        updateFormData('use_existing_codes', checked);
                        if (checked) {
                          updateFormData('create_new_qr_tags', false);
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {t('couponpassesinfo.use_existing_codes')}
                      <Tooltip title={t('Tooltips_explications.use_existing_codes')} arrow>
                        <InfoIcon sx={{ fontSize: 18, color: 'primary.main', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  }
                  sx={{ mt: 0, mb: 0 }}
                />
              </Box>
            </>
          )}

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('couponpassesinfo.list_of_coupon_passes')}
                <Tooltip title={t('Tooltips_explications.list_of_coupon_passes')} arrow>
                  <InfoIcon sx={{ fontSize: 18, color: 'primary.main', cursor: 'help' }} />
                </Tooltip>
              </Typography>
              <Button
                onClick={() => {
                  const quantity = formData.coupon_quantity || 10;
                  const newCodes = generateRandomCouponCodes(quantity);
                  setGeneratedCouponCodes(newCodes);
                  console.log('Códigos regenerados:', newCodes);
                }}
                variant="outlined"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.5,
                  fontSize: '0.75rem'
                }}
              >
                Regenerar
              </Button>
            </Box>
            <Box sx={{ 
              backgroundColor: '#f5f5f5', 
              border: '1px solid #e0e0e0', 
              borderRadius: 1, 
              p: 2, 
              maxHeight: 300, 
              overflow: 'auto' 
            }}>
              {generatedCouponCodes.map((code, index) => (
                <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                  {code}
                </Typography>
              ))}
              {generatedCouponCodes.length === 0 && (
                <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                  No hay códigos generados. Activa CouponPasses para ver los códigos.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      ref={formScrollRef}
      sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fafc 0%, #fdf7ee 100%)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        p: isMobile ? 0 : 2,
        pt: isMobile ? 0 : 1.5
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 1180, width: '100%', px: isMobile ? 1 : 2 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: isMobile ? 2 : 4,
            borderRadius: isMobile ? '22px 22px 0 0' : 5,
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            border: '1px solid #e7e5ea',
            boxShadow: '0 24px 70px -45px rgba(14, 15, 18, 0.45)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: isMobile ? '100vh' : 'calc(100vh - 40px)',
            width: '100%',
            '& .MuiTextField-root': {
              '& .MuiInputLabel-root': {
                color: '#6B6F7B',
                fontWeight: 600,
                letterSpacing: '-0.01em'
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#ED1790'
              }
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: '18px',
              backgroundColor: '#ffffff',
              transition: 'box-shadow 180ms ease, background-color 180ms ease',
              '& fieldset': {
                borderColor: '#d9dee8'
              },
              '&:hover fieldset': {
                borderColor: '#ED1790'
              },
              '&.Mui-focused': {
                backgroundColor: '#fff',
                boxShadow: '0 16px 34px -30px rgba(237, 23, 144, 0.9)'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#ED1790',
                borderWidth: '2px'
              }
            },
            '& .MuiInputBase-input': {
              fontSize: isMobile ? '0.95rem' : '1rem'
            },
            '& .MuiFormHelperText-root': {
              marginLeft: 0,
              fontSize: '0.78rem'
            },
            '& .MuiAlert-root': {
              borderRadius: '18px',
              border: '1px solid rgba(14, 15, 18, 0.08)',
              boxShadow: '0 14px 34px -30px rgba(14, 15, 18, 0.45)'
            },
            '& .MuiCard-root': {
              borderRadius: '22px',
              border: '1px solid #e7e5ea',
              boxShadow: '0 16px 42px -34px rgba(14, 15, 18, 0.42)',
              overflow: 'visible'
            },
            '& .MuiCardContent-root': {
              p: isMobile ? 2 : 2.5,
              '&:last-child': {
                pb: isMobile ? 2 : 2.5
              }
            },
            '& .MuiButton-root': {
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 800
            }
          }}
        >
          <Box sx={{ textAlign: 'center', mb: isMobile ? 2 : 2.5 }}>
            <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 800, mb: 0.75, color: '#1976d2', letterSpacing: '-0.04em' }}>
              {isEditMode ? t('forms.editContest') : t('forms.createContest')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#6B6F7B' }}>
              {isEditMode ? t('forms.editContestSubtitle') : t('forms.createContestSubtitle')}
            </Typography>
          </Box>

            {renderStepIndicator()}

          <Box sx={{ width: '100%', mb: isMobile ? 3 : 4, minHeight: 'auto', paddingBottom: isMobile ? 12 : 14 }}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
            {currentStep === 6 && renderStep6()}
            {currentStep === 7 && renderStep7()}
          </Box>

          <Divider sx={{ my: 1 }} />
          
          <Box 
            ref={buttonsContainerRef}
            sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            position: 'sticky',
            bottom: isMobile ? 8 : 12,
            backgroundColor: 'rgba(255, 255, 255, 0.94)',
            backdropFilter: 'blur(14px)',
            paddingTop: isMobile ? 2 : 2.5,
            paddingBottom: isMobile ? 2 : 2.5,
            paddingLeft: isMobile ? 2 : 3,
            paddingRight: isMobile ? 2 : 3,
            zIndex: 1000,
            border: '1px solid #e7e5ea',
            borderRadius: 999,
            boxShadow: isButtonsAtBottom ? '0 -18px 48px -34px rgba(14, 15, 18, 0.45)' : '0 18px 48px -34px rgba(14, 15, 18, 0.45)',
            marginTop: isMobile ? 5 : 8,
            mb: 0
          }}>
            <Button
                onClick={handlePrevious}
                disabled={currentStep === 1}
              variant="contained"
              color="success"
              sx={{ 
                minWidth: isMobile ? 112 : 140,
                opacity: currentStep === 1 ? 0.5 : 1,
                marginLeft: 0,
                px: isMobile ? 2 : 3,
                py: 1.2
                }}
              >
                {t('contestformbuttons.previous')}
            </Button>

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                {isEditMode && currentStep < steps.length && (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    sx={{
                      minWidth: isMobile ? 132 : 160,
                      px: isMobile ? 2 : 3,
                      py: 1.2,
                      backgroundColor: '#ED1790',
                      '&:hover': { backgroundColor: '#B01F8E' },
                    }}
                  >
                    Guardar Cambios
                  </Button>
                )}
                {currentStep < steps.length ? (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    color="primary"
                    sx={{
                      minWidth: isMobile ? 112 : 140,
                      marginRight: 0,
                      px: isMobile ? 2 : 3,
                      py: 1.2,
                      backgroundColor: '#ED1790',
                      '&:hover': { backgroundColor: '#B01F8E' },
                    }}
                  >
                    {t('contestformbuttons.next')}
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    startIcon={existingContest ? undefined : <AddIcon />}
                    sx={{
                      minWidth: isMobile ? 142 : 180,
                      marginRight: 0,
                      px: isMobile ? 2 : 3,
                      py: 1.2,
                      backgroundColor: '#ED1790',
                      '&:hover': { backgroundColor: '#B01F8E' },
                    }}
                  >
                    {existingContest ? 'Guardar Cambios' : t('contestformbuttons.create')}
                  </Button>
                )}
              </Box>
          </Box>
        </Paper>
      </Container>

      <Dialog open={restoreDraftOpen} onClose={() => setRestoreDraftOpen(false)}>
        <DialogTitle>{t('contestformdraft.restore_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('contestformdraft.restore_message')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardDraft} color="inherit">
            {t('contestformdraft.restore_discard')}
          </Button>
          <Button onClick={handleRestoreDraft} variant="contained" autoFocus>
            {t('contestformdraft.restore_confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeConfirmOpen} onClose={() => finishCloseRequest(false)}>
        <DialogTitle>{t('contestformdraft.close_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('contestformdraft.close_message')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => finishCloseRequest(false)} color="inherit">
            {t('contestformdraft.close_cancel')}
          </Button>
          <Button onClick={() => finishCloseRequest(true, 'discard')} color="warning">
            {t('contestformdraft.close_discard')}
          </Button>
          <Button onClick={() => finishCloseRequest(true, 'save')} variant="contained" autoFocus>
            {t('contestformdraft.close_save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default CreateContestForm;